export type Language = 'en' | 'ua' | 'ru' | 'ka'

export const translations = {
  en: {
    seo: {
      home: {
        title: 'ELVORA Consulting — Marine & Offshore Project Support',
        description:
          'Coordination, documentation, compliance and technical support for marine & offshore projects. For companies and seafarers.',
      },
      specialists: {
        title: 'ELVORA Consulting — Maritime Recruitment Support for Seafarers',
        description:
          'Professional recruitment support, profile review, and career opportunities for seafarers with verified shipowners and operators.',
      },
      companies: {
        title:
          'ELVORA Consulting — Crew Supply for Vessels & Offshore Projects',
        description:
          'Professional crew recruitment, screening, and management services for shipowners and operators. From single positions to full crew solutions.',
      },
    },
    header: {
      forCompanies: 'For Companies',
      forSeafarers: 'For Seafarers',
      sendCV: 'Submit CV',
      menu: 'Menu',
    },
    hero: {
      title:
        'ELVORA Consulting — Operational & Technical Support for Marine and Offshore Projects',
      description:
        'We support vessel owners, operators and technical teams with coordination, documentation & compliance, analytics, and urgent requests.',
      discussTask: 'Discuss a Request',
      submitProfile: 'Submit CV',
      tagline:
        'Focused on long-term collaboration with companies and specialists, not one-off requests.',
    },
    services: {
      pill: 'About',
      title: 'How We Support Marine\nand Offshore Operations',
      desc1:
        'We support companies and operators by solving operational, technical, and organizational tasks.',
      desc2:
        'From documentation and coordination to complex, non-standard requests — ELVORA delivers clear and practical solutions',
      items: {
        projectSupport: {
          title: 'Project & Operations Support',
          desc: 'Hands-on support for ongoing operations and projects',
        },
        documentation: {
          title: 'Documentation & Compliance',
          desc: 'Structured documentation, requirements and approvals',
        },
        technical: {
          title: 'Technical & Analytical Support',
          desc: 'Reporting, analysis and decision support',
        },
        coordination: {
          title: 'Process Coordination & Management',
          desc: 'Clear communication and control across stakeholders',
        },
        nonStandard: {
          title: 'Non-standard & Urgent Requests',
          desc: 'Fast response to complex and urgent tasks',
        },
      },
    },
    clients: {
      pill: 'Services',
      title: 'Services for Companies\nand Seafarers',
      desc1:
        'We handle operational, technical, and organizational tasks for marine and industrial projects.',
      desc2:
        'From documentation and coordination to complex, non-standard requests — we focus on solutions, not labels.',
      tabCompanies: 'For Companies',
      tabSpecialists: 'For Seafarers',
      learnMoreSpecialists: 'Learn More About Specialists',
      learnMoreCompanies: 'Learn More About Companies',
      specialistItems: [
        {
          id: '01',
          title: 'Profile Submission & Review',
          desc: 'We review professional profiles and documentation to match specialists with relevant tasks and projects',
        },
        {
          id: '02',
          title: 'Documentation Guidance',
          desc: 'Support with understanding requirements, documentation structure, and compliance expectations',
        },
        {
          id: '03',
          title: 'Project Coordination',
          desc: 'Clear communication throughout the process of task or project involvement',
        },
        {
          id: '04',
          title: 'Professional Support',
          desc: 'Guidance and assistance during cooperation with companies and project teams',
        },
        {
          id: '05',
          title: 'Long-Term Opportunities',
          desc: 'Building structured long-term professional cooperation based on experience and reliability',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'Project & Operational Support',
          desc: 'We support ongoing operations and projects by taking responsibility for specific tasks, coordination, and execution',
        },
        {
          id: '02',
          title: 'Documentation & Compliance',
          desc: 'Structured handling of documentation, requirements, and approvals according to project and regulatory needs',
        },
        {
          id: '03',
          title: 'Coordination & Process Management',
          desc: 'Clear communication and control between departments, contractors, and involved parties',
        },
        {
          id: '04',
          title: 'Technical & Analytical Support',
          desc: 'Analysis, reporting, and structured information to support operational and management decisions',
        },
        {
          id: '05',
          title: 'Non-Standard & Urgent Tasks',
          desc: 'Fast response to complex or time-sensitive requests that require experience and attention',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'Frequently Asked Questions',
      desc: 'For quick questions or clarifications, you can contact us via messenger.',
      items: [
        {
          id: '01',
          q: 'What types of tasks do you handle?',
          a: 'We work with operational, technical, organizational, and documentation-related tasks across marine, energy, and industrial projects.',
        },
        {
          id: '02',
          q: 'Who can contact ELVORA Consulting?',
          a: 'Shipowners, technical managers, operators, and industrial project leads can contact us for support with their operational challenges.',
        },
        {
          id: '03',
          q: 'Do you only work with offshore projects?',
          a: 'No, we work with both offshore and onshore projects across marine, energy, and industrial sectors.',
        },
        {
          id: '04',
          q: 'How do you start working on a request?',
          a: 'We begin with a brief consultation to understand your needs, then provide a clear proposal with timeline and deliverables.',
        },
        {
          id: '05',
          q: 'Is the information confidential?',
          a: 'Yes, all information is treated with strict confidentiality. We adhere to NDA protocols and data protection standards.',
        },
      ],
    },
    jobs: {
      pill: 'Vacancies',
      sendResume: 'Submit CV',
      items: [
        {
          title: 'Marine Engineer',
          type: 'Oil Tanker / VLCC',
          desc: 'Experienced marine engineer needed for VLCC operations. Responsible for main engine maintenance, troubleshooting, and ensuring compliance with safety regulations.',
        },
        {
          title: 'Chief Officer',
          type: 'Container Vessel',
          desc: 'Seeking qualified Chief Officer for container vessel operations. Responsibilities include cargo operations, navigation, safety management, and crew supervision.',
        },
        {
          title: 'Electrical Engineer',
          type: 'Offshore Platform',
          desc: 'Electrical engineer position available for offshore platform. Maintain and repair electrical systems, power generation equipment, and control systems.',
        },
        {
          title: 'Deck Cadet',
          type: 'Bulk Carrier',
          desc: 'Entry-level position for deck cadet on bulk carrier. Training opportunity for maritime academy graduates.',
        },
        {
          title: 'Second Engineer',
          type: 'LNG Carrier',
          desc: 'Second Engineer required for LNG carrier operations. Manage engine room operations, maintenance schedules, and technical documentation.',
        },
        {
          title: 'Bosun',
          type: 'Cruise Ship',
          desc: 'Experienced Bosun needed for luxury cruise operations. Lead deck crew, coordinate maintenance activities.',
        },
        {
          title: 'Marine Superintendent',
          type: 'Fleet Management',
          desc: 'Shore-based Marine Superintendent position. Oversee technical operations of vessel fleet, conduct inspections.',
        },
        {
          title: 'QHSE Manager',
          type: 'Offshore Installation',
          desc: 'Quality, Health, Safety & Environment Manager for offshore operations. Develop and implement QHSE policies.',
        },
        {
          title: 'AB Seaman',
          type: 'General Cargo',
          desc: 'Able Bodied Seaman position on general cargo vessel. Perform deck maintenance, cargo operations, and watchkeeping duties.',
        },
        {
          title: 'Chief Engineer',
          type: 'Chemical Tanker',
          desc: 'Chief Engineer position for chemical tanker operations. Overall responsibility for engine room and maintenance planning.',
        },
        {
          title: 'Radio Officer',
          type: 'Research Vessel',
          desc: 'Radio Officer needed for scientific research vessel. Maintain communication systems and support research operations.',
        },
        {
          title: 'Pumpman',
          type: 'Product Tanker',
          desc: 'Experienced Pumpman for product tanker operations. Responsible for cargo pumping systems and tank cleaning.',
        },
        {
          title: 'DPO',
          type: 'Offshore Support Vessel',
          desc: 'Dynamic Positioning Operator for OSV operations. Operate DP systems during offshore operations.',
        },
        {
          title: 'Crane Operator',
          type: 'Heavy Lift Vessel',
          desc: 'Certified crane operator for heavy lift vessel. Operate deck cranes for cargo operations.',
        },
        {
          title: 'Catering Manager',
          type: 'Passenger Ferry',
          desc: 'Catering Manager for passenger ferry operations. Manage galley operations, food service, and inventory.',
        },
      ],
    },
    contact: {
      title: 'Contact us',
      desc: 'Tell us about your task, challenge, or project. We will review the request and get back to you with a clear and practical solution',
      labels: {
        company: 'Company name',
        person: 'Contact person',
        email: 'Email address',
        phone: 'Phone / WhatsApp',
        task: 'Describe your task or request',
        project: 'Project / Vessel type',
        files: 'Attach files',
        dragDrop: 'Drag and drop files here, or click to browse',
        fileTypes: 'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (Max 48 MB)',
        phoneHint: 'Include country code (e.g., +1, +44, +380)',
      },
      button: 'Submit Request',
      disclaimer:
        'All information is treated confidentially. We do not share your data with third parties',
    },
    footer: {
      discussTask: 'Discuss a Request',
      sendProfile: 'Submit CV',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
    },
    specialists: {
      hero: {
        title: 'Maritime Recruitment Support for Seafarers',
        desc: 'This section is dedicated to seafarers seeking professional recruitment support, profile review, and access to career opportunities with shipowners and operators across marine and offshore sectors.',
        submitCV: 'Submit CV',
        viewOpportunities: 'View Current Opportunities',
        tagline1:
          'Connect with verified shipowners and operators through professional maritime recruitment support.',
        tagline2:
          'Submit your seafarer profile for structured review, documentation guidance, and consideration for marine and offshore opportunities.',
      },
      benefits: {
        pill: 'Support',
        title: 'Professional Recruitment\nSupport for Seafarers',
        desc1:
          'We provide structured recruitment support, profile screening, and documentation guidance for seafarers seeking employment with verified maritime and offshore operators.',
        desc2:
          'Submit your seafarer profile for structured review, documentation guidance, and consideration for marine and offshore opportunities.',
        items: {
          profile: {
            title: 'Profile Review & Screening',
            desc: 'Seafarer profiles are reviewed to assess rank, vessel experience, and documentation readiness before being considered for current or future opportunities.',
          },
          docs: {
            title: 'Document & Compliance Guidance',
            desc: 'Guidance on commonly required certificates, endorsements, and documentation requested by shipowners and offshore operators.',
          },
          matching: {
            title: 'Recruitment Matching Support',
            desc: 'Professional support in aligning seafarer profiles with recruitment requests based on experience, availability, and project requirements.',
          },
          access: {
            title: 'Access to Verified Opportunities',
            desc: 'Consideration for marine and offshore opportunities with shipowners and operators, subject to profile review and client requirements.',
          },
        },
      },
      steps: {
        pill: 'Process',
        title: 'How It Works',
        desc: 'Your path from application to placement',
        items: [
          {
            title: 'Profile Submission',
            desc: 'Submit your CV, rank, vessel experience, and availability for professional review and consideration.',
          },
          {
            title: 'Review & Screening',
            desc: 'We verify your documents and assess suitability for current and upcoming recruitment requests.',
          },
          {
            title: 'Recruitment Consideration',
            desc: 'Qualified profiles are matched to shipowner and operator requirements based on experience.',
          },
          {
            title: 'Direct Client Interaction',
            desc: 'If shortlisted, you communicate directly with the hiring company to finalize employment terms.',
          },
        ],
      },
      requirements: {
        pill: 'Documents',
        title: 'Required Documents for Seafarer Recruitment',
        desc: 'Standard documents commonly required during the seafarer recruitment and profile review process',
        items: [
          "Seafarer's Book",
          'Passport (valid)',
          'Certificate of Competency (CoC)',
          'STCW Certificates',
          'Medical Certificate (ENG1 / equivalent)',
          'BOSIET / Offshore Safety Certificates (if applicable)',
          'DP Certificate (for DP positions, if applicable)',
          'CV / Sea Service Records',
        ],
        note: 'The exact list of required documents may vary depending on the vessel type, project requirements, flag state, and client policies.',
      },
      faq: {
        pill: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          {
            q: 'Do you guarantee employment after submitting a CV?',
            a: 'No. Submitting a profile does not guarantee employment. All profiles are reviewed individually and may be considered for recruitment requests based on experience, availability, and client requirements.',
          },
          {
            q: 'Do you act as a crewing or manning agency?',
            a: 'No. Elvora provides recruitment support and advisory services. Employment decisions and contracts are handled directly between seafarers and hiring companies.',
          },
          {
            q: 'Do you charge seafarers any fees?',
            a: 'We do not charge seafarers for submitting profiles. Some optional services, such as documentation guidance or career consulting, may be offered separately if requested.',
          },
          {
            q: 'What types of vessels and projects do you work with?',
            a: 'We support recruitment requests across marine and offshore sectors, including merchant vessels, offshore support vessels, and project-based operations.',
          },
          {
            q: 'How long does the review process take?',
            a: 'Profile reviews depend on current recruitment activity and client requests. Only shortlisted candidates are contacted.',
          },
          {
            q: 'How will I be contacted if my profile is suitable?',
            a: 'Shortlisted candidates are contacted directly using the contact details provided during profile submission.',
          },
        ],
      },
      form: {
        pill: 'Application',
        title: 'Submit Your Seafarer Profile',
        desc1:
          'Submit your professional profile for review as part of the recruitment support process.',
        desc2: 'Submission does not guarantee employment.',
        labels: {
          firstName: 'First Name',
          lastName: 'Last Name',
          rank: 'Rank / Position',
          selectRank: 'Select rank/position',
          specify: 'Please specify',
          enterPosition: 'Enter your position',
          email: 'Email',
          phone: 'Phone / WhatsApp',
          phoneHint: 'Include country code (e.g., +1, +44, +380)',
          cv: 'CV Upload (PDF, DOC, or Image)',
          dragDrop: 'Drag and drop your CV here, or click to browse',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (Max 48 MB)',
          vesselType: 'Vessel Type Experience',
          selectVessel: 'Select vessel type',
          yearsExp: 'Years of Experience',
          availability: 'Availability Date',
          availabilityHint: 'Select your earliest availability date',
          consent:
            'I confirm that the information provided is accurate and agree to the processing of my personal data for recruitment support purposes.',
          submit: 'Submit Profile',
          disclaimer:
            'All information is treated confidentially. Only shortlisted candidates are contacted.',
        },
        ranks: {
          deck: 'Deck Department',
          engine: 'Engine Department',
          other: 'Other',
          options: {
            master: 'Master',
            chiefMate: 'Chief Mate',
            secondMate: 'Second Mate',
            thirdMate: 'Third Mate',
            bosun: 'Bosun',
            ab: 'Able Seaman (AB)',
            os: 'Ordinary Seaman (OS)',
            chiefEng: 'Chief Engineer',
            secondEng: 'Second Engineer',
            thirdEng: 'Third Engineer',
            fourthEng: 'Fourth Engineer',
            eto: 'ETO',
            asstEto: 'Assistant ETO',
            motorman: 'Motorman / Oiler',
            wiper: 'Wiper',
            dpo: 'DPO',
            sdpo: 'SDPO',
            craneOp: 'Crane Operator',
            rigger: 'Rigger',
            electrician: 'Electrician',
            mechanic: 'Mechanic',
            other: 'Other (please specify)',
          },
        },
        vessels: {
          tanker: 'Tanker',
          bulk: 'Bulk Carrier',
          container: 'Container Vessel',
          offshore: 'Offshore Support Vessel',
          cruise: 'Cruise Ship',
          other: 'Other',
        },
      },
      contact: {
        pill: 'Contact',
        title: 'Need Help?',
        desc: 'Contact us via messenger or email for quick assistance',
      },
    },
    companies: {
      hero: {
        title: 'Reliable Crew Supply for Vessels & Offshore Projects',
        desc1:
          'Professional recruitment, screening, and crew management services for shipowners and operators.',
        desc2: 'From single positions to full crew solutions.',
        requestCrew: 'Request Crew',
        discussTask: 'Discuss a Request',
      },
      services: {
        pill: 'Services',
        title: 'Services for Shipowners\n& Operators',
        desc1:
          'Comprehensive crew recruitment and management solutions for maritime and offshore operations.',
        desc2:
          'From candidate selection to full compliance and crew logistics.',
        items: {
          recruitment: {
            title: 'Crew Recruitment',
            desc: 'Full-cycle recruitment for all ranks and vessel types — offshore and merchant fleet.',
          },
          screening: {
            title: 'Screening & Verification',
            desc: 'Document verification, background checks, experience validation, and compliance screening.',
          },
          compliance: {
            title: 'Compliance Management',
            desc: 'STCW, MLC, flag state and client-specific requirements fully covered.',
          },
          coordination: {
            title: 'Crew Coordination',
            desc: 'Crew changes, travel arrangements, mobilization, and replacement planning.',
          },
        },
      },
      steps: {
        pill: 'Process',
        title: 'How We Work',
        desc: 'Clear and transparent crew recruitment process — from request to deployment',
        items: [
          {
            title: 'Request',
            desc: 'Submit your crew requirements and vessel or project details',
          },
          {
            title: 'Screening',
            desc: 'Candidate selection, document verification, and compliance checks',
          },
          {
            title: 'Matching & Interview',
            desc: 'Shortlisting qualified candidates and arranging interviews',
          },
          {
            title: 'Deployment',
            desc: 'Contract signing, crew mobilization, and ongoing coordination',
          },
        ],
      },
      compliance: {
        pill: 'Compliance',
        title: 'Compliance & Confidentiality',
        desc: 'We operate in full compliance with international maritime standards and ensure strict data protection',
        items: {
          nda: {
            title: 'NDA & Data Protection',
            desc: 'All client and crew information is handled under strict confidentiality and non-disclosure principles.',
          },
          verification: {
            title: 'Document Verification',
            desc: 'Thorough verification of certificates, licenses, medicals, and experience before submission.',
          },
          stcw: {
            title: 'STCW & MLC Compliance',
            desc: 'Full compliance with STCW, MLC 2006, flag state, and client-specific requirements.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          {
            q: 'Do you act as an employer or crewing agency?',
            a: 'No. Elvora provides recruitment and operational support services. Employment contracts are signed directly between the crew and the hiring company.',
          },
          {
            q: 'What types of vessels and projects do you support?',
            a: 'We support merchant vessels and offshore projects, including tankers, offshore support vessels, and project-based marine operations.',
          },
          {
            q: 'Do you provide single crew members or full crews?',
            a: 'We support both single-position requests and full or partial crew solutions, depending on project scope and client requirements.',
          },
          {
            q: 'How do you ensure candidate quality and compliance?',
            a: 'All candidates undergo document verification, experience review, and compliance checks in line with STCW, MLC, flag state, and client-specific standards.',
          },
          {
            q: 'How quickly can crew be mobilized?',
            a: 'Mobilization timelines depend on vessel type, position, availability, and documentation status. Timelines are discussed individually for each request.',
          },
          {
            q: 'Do you work worldwide?',
            a: 'Yes. We support international projects and cooperate with shipowners and operators across multiple regions.',
          },
        ],
      },
      form: {
        pill: 'Request',
        title: 'Request Crew or Discuss Your Project',
        desc1: 'Submit your crew requirements or project details.',
        desc2: 'We review each request individually and respond accordingly.',
        sections: {
          company: 'Company Information',
          details: 'Request Details',
          timeline: 'Timeline',
        },
        labels: {
          companyName: 'Company Name',
          contactPerson: 'Contact Person',
          email: 'Email',
          phone: 'Phone / WhatsApp',
          vesselType: 'Vessel / Project Type',
          selectVessel: 'Select vessel/project type',
          positions: 'Positions Required',
          selectPositions: 'Select positions',
          crewNumber: 'Number of Crew',
          location: 'Location / Region',
          selectLocation: 'Select region (optional)',
          additional: 'Additional Requirements',
          additionalPlaceholder:
            'Specific certifications, experience requirements, project details...',
          startDate: 'Desired Start Date',
          duration: 'Contract Duration',
          selectDuration: 'Select duration (optional)',
          consent:
            'I confirm that the information provided is accurate and agree to the processing of data for recruitment and operational support purposes.',
          submit: 'Submit Request',
          disclaimer:
            'All information is treated confidentially under NDA. We respond within 24 hours.',
        },
        vesselTypes: [
          'Merchant Vessel',
          'Tanker',
          'Offshore Support Vessel (OSV)',
          'Jack-Up / Offshore Unit',
          'Project-Based Operation',
          'Other',
        ],
        regions: [
          'Worldwide',
          'Europe',
          'Middle East',
          'Africa',
          'Asia-Pacific',
          'Other',
        ],
        durations: {
          m1: '1 month',
          m2: '2 months',
          m3: '3 months',
          m4: '4 months',
          m6: '6 months',
          m9: '9 months',
          m12: '12 months',
          ongoing: 'Ongoing / Permanent',
          project: 'Project-based',
        },
      },
      contact: {
        pill: 'Contact',
        title: 'Get in Touch',
        desc: 'Contact us for crew requests, consultations, or partnership opportunities',
      },
    },
  },
  ua: {
    seo: {
      home: {
        title: 'ELVORA Consulting — підтримка морських і офшорних проєктів',
        description:
          'ELVORA Consulting: координація, документація, compliance та техпідтримка для морських і офшорних проєктів. Для компаній і моряків.',
      },
      specialists: {
        title: 'ELVORA Consulting — підтримка працевлаштування моряків',
        description:
          'Професійна підтримка у працевлаштуванні, скринінг профілю та консультації з документації для моряків.',
      },
      companies: {
        title:
          'ELVORA Consulting — підбір екіпажів для суден та офшорних проєктів',
        description:
          'Професійний рекрутинг, скринінг та управління екіпажами для судновласників та операторів.',
      },
    },
    header: {
      forCompanies: 'Для Компаній',
      forSeafarers: 'Для Моряків',
      sendCV: 'Надіслати резюме',
      menu: 'Меню',
    },
    hero: {
      title:
        'ELVORA Consulting — операційна та технічна підтримка морських і офшорних проєктів',
      description:
        'Допомагаємо судновласникам, операторам і технічним командам: координація робіт, документація та відповідність вимогам (compliance), аналітика й термінові запити.',
      discussTask: 'Обговорити завдання',
      submitProfile: 'Надіслати резюме',
      tagline:
        'Працюємо на довгострокову співпрацю з компаніями та фахівцями, а не на разові заявки.',
    },
    services: {
      pill: 'Про Нас',
      title: 'Як ми підтримуємо морські\nта офшорні операції',
      desc1:
        'Ми підтримуємо компанії та операторів, вирішуючи операційні, технічні та організаційні завдання.',
      desc2:
        'Від документації та координації до складних, нестандартних запитів — ELVORA надає чіткі та практичні рішення',
      items: {
        projectSupport: {
          title: 'Проєктна та операційна підтримка',
          desc: 'Комплексна допомога для поточних операцій і проєктів',
        },
        documentation: {
          title: 'Документація та відповідність вимогам (Compliance)',
          desc: 'Структурування документів, вимог і погоджень',
        },
        technical: {
          title: 'Технічна та аналітична підтримка',
          desc: 'Аналіз, звітність і підтримка рішень',
        },
        coordination: {
          title: 'Координація та управління процесами',
          desc: 'Чітка комунікація й контроль між усіма сторонами',
        },
        nonStandard: {
          title: 'Нестандартні та термінові задачі',
          desc: 'Швидка реакція на складні та термінові запити',
        },
      },
    },
    clients: {
      pill: 'Послуги',
      title: 'Послуги для компаній\nі моряків',
      desc1:
        'Ми виконуємо операційні, технічні та організаційні завдання для морських та промислових проектів.',
      desc2:
        'Від документації та координації до складних, нестандартних запитів — ми фокусуємось на рішеннях, а не ярликах.',
      tabCompanies: 'Для Компаній',
      tabSpecialists: 'Для Моряків',
      learnMoreSpecialists: 'Дізнатися Більше Про Спеціалістів',
      learnMoreCompanies: 'Дізнатися Більше Про Компанії',
      specialistItems: [
        {
          id: '01',
          title: 'Подання та Перегляд Профілю',
          desc: 'Ми переглядаємо професійні профілі та документацію, щоб підібрати спеціалістів до відповідних завдань та проектів',
        },
        {
          id: '02',
          title: 'Консультації з Документації',
          desc: 'Підтримка у розумінні вимог, структури документації та очікувань щодо відповідності',
        },
        {
          id: '03',
          title: 'Координація Проектів',
          desc: 'Чітка комунікація протягом процесу залучення до завдань або проектів',
        },
        {
          id: '04',
          title: 'Професійна Підтримка',
          desc: 'Керівництво та допомога під час співпраці з компаніями та проектними командами',
        },
        {
          id: '05',
          title: 'Довгострокові Можливості',
          desc: 'Побудова структурованої довгострокової професійної співпраці на основі досвіду та надійності',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'Проектна та Операційна Підтримка',
          desc: 'Ми підтримуємо поточні операції та проекти, беручи на себе відповідальність за конкретні завдання, координацію та виконання',
        },
        {
          id: '02',
          title: 'Документація та Відповідність',
          desc: 'Структурована обробка документації, вимог та затверджень відповідно до проектних та регуляторних потреб',
        },
        {
          id: '03',
          title: 'Координація та Управління Процесами',
          desc: 'Чітка комунікація та контроль між відділами, підрядниками та залученими сторонами',
        },
        {
          id: '04',
          title: 'Технічна та Аналітична Підтримка',
          desc: 'Аналіз, звітність та структурована інформація для підтримки операційних та управлінських рішень',
        },
        {
          id: '05',
          title: 'Нестандартні та Термінові Завдання',
          desc: 'Швидка реакція на складні або термінові запити, які потребують досвіду та уваги',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'Поширені запитання',
      desc: "Для швидких запитань або уточнень ви можете зв'язатися з нами через месенджер.",
      items: [
        {
          id: '01',
          q: 'Які типи завдань ви виконуєте?',
          a: 'Ми працюємо з операційними, технічними, організаційними та документальними завданнями в морських, енергетичних та промислових проектах.',
        },
        {
          id: '02',
          q: 'Хто може звернутися до ELVORA?',
          a: 'Судновласники, технічні менеджери, оператори та керівники промислових проектів можуть звертатися до нас за підтримкою у вирішенні операційних завдань.',
        },
        {
          id: '03',
          q: 'Ви працюєте тільки з офшорними проектами?',
          a: 'Ні, ми працюємо як з офшорними, так і з береговими проектами в морському, енергетичному та промисловому секторах.',
        },
        {
          id: '04',
          q: 'Як ви починаєте роботу над запитом?',
          a: 'Ми починаємо з короткої консультації, щоб зрозуміти ваші потреби, після чого надаємо чітку пропозицію з термінами та результатами.',
        },
        {
          id: '05',
          q: 'Чи є інформація конфіденційною?',
          a: 'Так, вся інформація обробляється з суворою конфіденційністю. Ми дотримуємось протоколів NDA та стандартів захисту даних.',
        },
      ],
    },
    jobs: {
      pill: 'Вакансії',
      sendResume: 'Надіслати резюме',
      items: [
        {
          title: 'Морський інженер',
          type: 'Нафтовий танкер / VLCC',
          desc: 'Досвідчений морський інженер для роботи на VLCC. Відповідальність за обслуговування головного двигуна, усунення несправностей та забезпечення дотримання правил безпеки.',
        },
        {
          title: 'Старший помічник капітана',
          type: 'Контейнеровоз',
          desc: "Шукаємо кваліфікованого старшого помічника для контейнеровоза. Обов'язки включають вантажні операції, навігацію, управління безпекою та нагляд за екіпажем.",
        },
        {
          title: 'Інженер-електрик',
          type: 'Офшорна платформа',
          desc: 'Позиція інженера-електрика на офшорній платформі. Обслуговування та ремонт електричних систем, обладнання для генерації електроенергії та систем управління.',
        },
        {
          title: 'Палубний кадет',
          type: 'Балкер',
          desc: 'Початкова позиція для палубного кадета на балкері. Можливість навчання для випускників морських академій.',
        },
        {
          title: 'Другий механік',
          type: 'Газовоз LNG',
          desc: 'Потрібен другий механік для роботи на LNG-газовозі. Управління операціями в машинному відділенні, графіками технічного обслуговування та технічною документацією.',
        },
        {
          title: 'Боцман',
          type: 'Круїзне судно',
          desc: 'Досвідчений боцман для роботи на круїзному лайнері. Керівництво палубною командою, координація робіт з технічним обслуговуванням.',
        },
        {
          title: 'Морський суперінтендант',
          type: 'Управління флотом',
          desc: 'Берегова позиція морського суперінтенданта. Нагляд за технічними операціями флоту, проведення інспекцій.',
        },
        {
          title: 'Менеджер QHSE',
          type: 'Офшорна установка',
          desc: "Менеджер з якості, охорони здоров'я, безпеки та екології для офшорних операцій. Розробка та впровадження політики QHSE.",
        },
        {
          title: 'Матрос 1 класу',
          type: 'Генеральний вантаж',
          desc: 'Позиція матроса першого класу на судні генеральних вантажів. Виконання палубних робіт, вантажних операцій та несення вахти.',
        },
        {
          title: 'Старший механік',
          type: 'Хімвоз',
          desc: 'Позиція старшого механіка для хімвоза. Загальна відповідальність за машинне відділення та планування технічного обслуговування.',
        },
        {
          title: 'Радіооператор',
          type: 'Дослідницьке судно',
          desc: "Потрібен радіооператор для науково-дослідного судна. Обслуговування систем зв'язку та підтримка дослідницьких операцій.",
        },
        {
          title: 'Пампмен',
          type: 'Продуктовоз',
          desc: 'Досвідчений пампмен для продуктовоза. Відповідальність за вантажні насосні системи та мийку танків.',
        },
        {
          title: 'Оператор DP',
          type: 'Судно забезпечення (OSV)',
          desc: 'Оператор динамічного позиціонування для OSV. Управління системами DP під час офшорних операцій.',
        },
        {
          title: 'Кранівник',
          type: 'Важковантажне судно',
          desc: 'Сертифікований кранівник для судна великої вантажопідйомності. Управління палубними кранами для вантажних операцій.',
        },
        {
          title: 'Менеджер з кейтерингу',
          type: 'Пасажирський пором',
          desc: 'Менеджер з кейтерингу для пасажирського порома. Управління роботою камбуза, обслуговуванням харчування та інвентаризацією.',
        },
      ],
    },
    contact: {
      title: "Зв'яжіться з нами",
      desc: 'Розкажіть про ваше завдання, виклик або проект. Ми розглянемо запит і повернемося з чітким та практичним рішенням',
      labels: {
        company: 'Назва компанії',
        person: 'Контактна особа',
        email: 'Електронна пошта',
        phone: 'Телефон / WhatsApp',
        task: 'Опишіть ваше завдання або запит',
        project: 'Тип проекту / судна',
        files: 'Прикріпити файли',
        dragDrop: 'Перетягніть файли сюди або натисніть для вибору',
        fileTypes:
          'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (Макс. 48 МБ)',
        phoneHint: 'Вкажіть код країни (наприклад, +1, +44, +380)',
      },
      button: 'Надіслати Запит',
      disclaimer:
        'Вся інформація є конфіденційною. Ми не передаємо ваші дані третім сторонам',
    },
    footer: {
      discussTask: 'Обговорити завдання',
      sendProfile: 'Надіслати резюме',
      privacy: 'Політика Конфіденційності',
      terms: 'Умови Використання',
    },
    specialists: {
      hero: {
        title: 'Підтримка Працевлаштування Моряків',
        desc: "Цей розділ присвячений морякам, які шукають професійну підтримку у працевлаштуванні, перегляд профілю та доступ до кар'єрних можливостей у судновласників та операторів морського та офшорного секторів.",
        submitCV: 'Надіслати резюме',
        viewOpportunities: 'Переглянути Вакансії',
        tagline1:
          "Зв'язок з перевіреними судновласниками та операторами через професійну підтримку.",
        tagline2:
          'Надішліть свій профіль для структурованого перегляду, консультацій з документації та розгляду можливостей.',
      },
      benefits: {
        pill: 'Підтримка',
        title: 'Професійна Підтримка\nПрацевлаштування Моряків',
        desc1:
          'Ми надаємо структуровану підтримку у працевлаштуванні, скринінг профілю та консультації з документації для моряків, які шукають роботу у перевірених морських та офшорних операторів.',
        desc2:
          'Надішліть свій профіль для структурованого перегляду, консультацій з документації та розгляду можливостей.',
        items: {
          profile: {
            title: 'Перегляд та Скринінг Профілю',
            desc: 'Профілі моряків переглядаються для оцінки рангу, досвіду на суднах та готовності документів перед розглядом для поточних або майбутніх можливостей.',
          },
          docs: {
            title: 'Консультації з Документації',
            desc: 'Консультації щодо необхідних сертифікатів, підтверджень та документації, яку вимагають судновласники та офшорні оператори.',
          },
          matching: {
            title: 'Підтримка у Підборі Вакансій',
            desc: 'Професійна підтримка у відповідності профілів моряків до запитів на найм на основі досвіду, доступності та вимог проекту.',
          },
          access: {
            title: 'Доступ до Перевірених Можливостей',
            desc: 'Розгляд для морських та офшорних можливостей у судновласників та операторів за умови перегляду профілю та відповідності вимогам клієнта.',
          },
        },
      },
      steps: {
        pill: 'Процес',
        title: 'Як Це Працює',
        desc: 'Ваш шлях від заявки до працевлаштування',
        items: [
          {
            title: 'Подання Профілю',
            desc: 'Надішліть своє CV, ранг, досвід на суднах та доступність для професійного перегляду та розгляду.',
          },
          {
            title: 'Перегляд та Скринінг',
            desc: 'Ми перевіряємо ваші документи та оцінюємо відповідність для поточних та майбутніх запитів на найм.',
          },
          {
            title: 'Розгляд для Працевлаштування',
            desc: 'Кваліфіковані профілі підбираються під вимоги судновласників та операторів на основі досвіду.',
          },
          {
            title: 'Пряма Взаємодія з Клієнтом',
            desc: 'Якщо вашу кандидатуру відібрано, ви спілкуєтесь безпосередньо з компанією-роботодавцем для узгодження умов працевлаштування.',
          },
        ],
      },
      requirements: {
        pill: 'Документи',
        title: 'Необхідні Документи для Працевлаштування',
        desc: 'Стандартні документи, які зазвичай вимагаються під час процесу найму та перегляду профілю моряка',
        items: [
          "Паспорт моряка (Seafarer's Book)",
          'Закордонний паспорт (дійсний)',
          'Робочий диплом (CoC)',
          'Сертифікати STCW',
          'Медичний сертифікат (ENG1 / еквівалент)',
          'BOSIET / Офшорні сертифікати безпеки (якщо застосовно)',
          'Сертифікат DP (для позицій DP, якщо застосовно)',
          'CV / Послужна книжка',
        ],
        note: 'Точний перелік необхідних документів може відрізнятися залежно від типу судна, вимог проекту, держави прапора та політики клієнта.',
      },
      faq: {
        pill: 'FAQ',
        title: 'Часті Запитання',
        items: [
          {
            q: 'Чи гарантуєте ви працевлаштування після подання CV?',
            a: 'Ні. Подання профілю не гарантує працевлаштування. Всі профілі розглядаються індивідуально і можуть бути враховані для запитів на найм на основі досвіду, доступності та вимог клієнта.',
          },
          {
            q: 'Ви дієте як крюїнгова агенція?',
            a: 'Ні. Elvora надає підтримку у працевлаштуванні та консультаційні послуги. Рішення про найм та контракти укладаються безпосередньо між моряками та компаніями-роботодавцями.',
          },
          {
            q: 'Чи стягуєте ви плату з моряків?',
            a: "Ми не стягуємо плату з моряків за подання профілів. Деякі додаткові послуги, такі як консультації з документації або кар'єрний консалтинг, можуть пропонуватися окремо за запитом.",
          },
          {
            q: 'З якими типами суден та проектів ви працюєте?',
            a: 'Ми підтримуємо запити на найм у морському та офшорному секторах, включаючи торговельні судна, судна офшорного флоту та проектні операції.',
          },
          {
            q: 'Скільки часу займає процес розгляду?',
            a: "Перегляд профілю залежить від поточної активності найму та запитів клієнтів. Зв'язуються лише з відібраними кандидатами.",
          },
          {
            q: "Як зі мною зв'яжуться, якщо мій профіль підходить?",
            a: "З відібраними кандидатами зв'язуються безпосередньо, використовуючи контактні дані, надані під час подання профілю.",
          },
        ],
      },
      form: {
        pill: 'Заявка',
        title: 'Надіслати Профіль Моряка',
        desc1:
          'Надішліть свій професійний профіль для перегляду в рамках процесу підтримки працевлаштування.',
        desc2: 'Подання не гарантує працевлаштування.',
        labels: {
          firstName: "Ім'я",
          lastName: 'Прізвище',
          rank: 'Ранг / Позиція',
          selectRank: 'Оберіть ранг/позицію',
          specify: 'Будь ласка, уточніть',
          enterPosition: 'Введіть вашу позицію',
          email: 'Email',
          phone: 'Телефон / WhatsApp',
          phoneHint: 'Вкажіть код країни (наприклад, +1, +44, +380)',
          cv: 'Завантаження CV (PDF, DOC або Зображення)',
          dragDrop: 'Перетягніть ваше CV сюди або натисніть для вибору',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (Макс. 48 МБ)',
          vesselType: 'Досвід за Типом Судна',
          selectVessel: 'Оберіть тип судна',
          yearsExp: 'Років Досвіду',
          availability: 'Дата Готовності',
          availabilityHint: 'Оберіть найранішу дату готовності',
          consent:
            'Я підтверджую, що надана інформація є точною, і погоджуюсь на обробку моїх персональних даних для цілей підтримки працевлаштування.',
          submit: 'Надіслати Профіль',
          disclaimer:
            "Вся інформація є конфіденційною. Зв'язуються лише з відібраними кандидатами.",
        },
        ranks: {
          deck: 'Палубна Команда',
          engine: 'Машинна Команда',
          other: 'Інше',
          options: {
            master: 'Капітан (Master)',
            chiefMate: 'Старший Помічник (Chief Mate)',
            secondMate: 'Другий Помічник (Second Mate)',
            thirdMate: 'Третій Помічник (Third Mate)',
            bosun: 'Боцман (Bosun)',
            ab: 'Матрос 1 класу (AB)',
            os: 'Матрос 2 класу (OS)',
            chiefEng: 'Старший Механік (Chief Engineer)',
            secondEng: 'Другий Механік (Second Engineer)',
            thirdEng: 'Третій Механік (Third Engineer)',
            fourthEng: 'Четвертий Механік (Fourth Engineer)',
            eto: 'Електромеханік (ETO)',
            asstEto: 'Асистент Електромеханіка (Assistant ETO)',
            motorman: 'Моторист (Motorman / Oiler)',
            wiper: 'Вайпер (Wiper)',
            dpo: 'Оператор DP (DPO)',
            sdpo: 'Старший Оператор DP (SDPO)',
            craneOp: 'Кранівник (Crane Operator)',
            rigger: 'Стропальник (Rigger)',
            electrician: 'Електрик (Electrician)',
            mechanic: 'Механік (Mechanic)',
            other: 'Інше (будь ласка, уточніть)',
          },
        },
        vessels: {
          tanker: 'Танкер',
          bulk: 'Балкер',
          container: 'Контейнеровоз',
          offshore: 'Судно Офшорного Флоту (OSV)',
          cruise: 'Круїзне Судно',
          other: 'Інше',
        },
      },
      contact: {
        pill: 'Контакти',
        title: 'Потрібна Допомога?',
        desc: "Зв'яжіться з нами через месенджер або email для швидкої допомоги",
      },
    },
    companies: {
      hero: {
        title: 'Надійне Постачання Екіпажів для Суден та Офшорних Проектів',
        desc1:
          'Професійний рекрутинг, скринінг та управління екіпажами для судновласників та операторів.',
        desc2: 'Від окремих позицій до повних екіпажів.',
        requestCrew: 'Запит на Екіпаж',
        discussTask: 'Обговорити Завдання',
      },
      services: {
        pill: 'Послуги',
        title: 'Послуги для Судновласників\nта Операторів',
        desc1:
          'Комплексні рішення з підбору та управління екіпажем для морських та офшорних операцій.',
        desc2:
          'Від відбору кандидатів до повної відповідності вимогам та логістики екіпажу.',
        items: {
          recruitment: {
            title: 'Підбір Екіпажу',
            desc: 'Повний цикл рекрутингу для всіх рангів та типів суден — офшорний та торговельний флот.',
          },
          screening: {
            title: 'Скринінг та Перевірка',
            desc: 'Перевірка документів, біографії, підтвердження досвіду та скринінг на відповідність вимогам.',
          },
          compliance: {
            title: 'Управління Відповідністю',
            desc: 'Повне покриття вимог STCW, MLC, держави прапора та специфічних вимог клієнта.',
          },
          coordination: {
            title: 'Координація Екіпажу',
            desc: 'Зміни екіпажу, організація подорожей, мобілізація та планування замін.',
          },
        },
      },
      steps: {
        pill: 'Процес',
        title: 'Як Ми Працюємо',
        desc: 'Чіткий та прозорий процес підбору екіпажу — від запиту до посадки на судно',
        items: [
          {
            title: 'Запит',
            desc: 'Надішліть ваші вимоги до екіпажу та деталі судна або проекту',
          },
          {
            title: 'Скринінг',
            desc: 'Відбір кандидатів, перевірка документів та відповідності вимогам',
          },
          {
            title: "Підбір та Інтерв'ю",
            desc: "Формування короткого списку кваліфікованих кандидатів та організація інтерв'ю",
          },
          {
            title: 'Мобілізація',
            desc: 'Підписання контракту, мобілізація екіпажу та поточна координація',
          },
        ],
      },
      compliance: {
        pill: 'Відповідність',
        title: 'Відповідність та Конфіденційність',
        desc: 'Ми працюємо у повній відповідності до міжнародних морських стандартів та забезпечуємо суворий захист даних',
        items: {
          nda: {
            title: 'NDA та Захист Даних',
            desc: 'Вся інформація клієнтів та екіпажу обробляється з дотриманням суворої конфіденційності та принципів нерозголошення.',
          },
          verification: {
            title: 'Перевірка Документів',
            desc: 'Ретельна перевірка сертифікатів, ліцензій, медичних довідок та досвіду перед поданням.',
          },
          stcw: {
            title: 'Відповідність STCW та MLC',
            desc: 'Повна відповідність вимогам STCW, MLC 2006, держави прапора та специфічним вимогам клієнта.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'Часті Запитання',
        items: [
          {
            q: 'Ви дієте як роботодавець або крюїнгова агенція?',
            a: 'Ні. Elvora надає послуги з рекрутингу та операційної підтримки. Трудові договори підписуються безпосередньо між екіпажем та компанією-роботодавцем.',
          },
          {
            q: 'Які типи суден та проектів ви підтримуєте?',
            a: 'Ми підтримуємо торговельні судна та офшорні проекти, включаючи танкери, судна офшорного флоту та проектні морські операції.',
          },
          {
            q: 'Ви надаєте окремих членів екіпажу чи повні екіпажі?',
            a: 'Ми підтримуємо як запити на окремі позиції, так і рішення для повних або часткових екіпажів, залежно від обсягу проекту та вимог клієнта.',
          },
          {
            q: 'Як ви забезпечуєте якість кандидатів та відповідність вимогам?',
            a: 'Всі кандидати проходять перевірку документів, огляд досвіду та перевірку на відповідність стандартам STCW, MLC, держави прапора та клієнта.',
          },
          {
            q: 'Як швидко може бути мобілізований екіпаж?',
            a: 'Терміни мобілізації залежать від типу судна, позиції, доступності та статусу документів. Терміни обговорюються індивідуально для кожного запиту.',
          },
          {
            q: 'Ви працюєте по всьому світу?',
            a: 'Так. Ми підтримуємо міжнародні проекти та співпрацюємо з судновласниками та операторами в різних регіонах.',
          },
        ],
      },
      form: {
        pill: 'Запит',
        title: 'Запит на Екіпаж або Обговорення Проекту',
        desc1: 'Надішліть ваші вимоги до екіпажу або деталі проекту.',
        desc2: 'Ми розглядаємо кожен запит індивідуально та надаємо відповідь.',
        sections: {
          company: 'Інформація про Компанію',
          details: 'Деталі Запиту',
          timeline: 'Терміни',
        },
        labels: {
          companyName: 'Назва Компанії',
          contactPerson: 'Контактна Особа',
          email: 'Email',
          phone: 'Телефон / WhatsApp',
          vesselType: 'Тип Судна / Проекту',
          selectVessel: 'Оберіть тип судна/проекту',
          positions: 'Необхідні Позиції',
          selectPositions: 'Оберіть позиції',
          crewNumber: 'Кількість Екіпажу',
          location: 'Локація / Регіон',
          selectLocation: 'Оберіть регіон (опціонально)',
          additional: 'Додаткові Вимоги',
          additionalPlaceholder:
            'Специфічні сертифікати, вимоги до досвіду, деталі проекту...',
          startDate: 'Бажана Дата Початку',
          duration: 'Тривалість Контракту',
          selectDuration: 'Оберіть тривалість (опціонально)',
          consent:
            'Я підтверджую, що надана інформація є точною, і погоджуюсь на обробку даних для цілей рекрутингу та операційної підтримки.',
          submit: 'Надіслати Запит',
          disclaimer:
            'Вся інформація обробляється конфіденційно згідно з NDA. Ми відповідаємо протягом 24 годин.',
        },
        vesselTypes: [
          'Торговельне Судно',
          'Танкер',
          'Судно Офшорного Флоту (OSV)',
          'Jack-Up / Офшорна Установка',
          'Проектна Операція',
          'Інше',
        ],
        regions: [
          'Весь Світ',
          'Європа',
          'Близький Схід',
          'Африка',
          'Азійсько-Тихоокеанський регіон',
          'Інше',
        ],
        durations: {
          m1: '1 місяць',
          m2: '2 місяці',
          m3: '3 місяці',
          m4: '4 місяці',
          m6: '6 місяців',
          m9: '9 місяців',
          m12: '12 місяців',
          ongoing: 'Постійна / Безстрокова',
          project: 'Проектна',
        },
      },
      contact: {
        pill: 'Контакти',
        title: "Зв'яжіться з Нами",
        desc: "Зв'яжіться з нами для запитів на екіпаж, консультацій або можливостей партнерства",
      },
    },
  },
  ru: {
    seo: {
      home: {
        title: 'ELVORA Consulting — поддержка морских и офшорных проектов',
        description:
          'ELVORA Consulting: координация, документация, compliance и техподдержка для морских и офшорных проектов. Для компаний и моряков.',
      },
      specialists: {
        title: 'ELVORA Consulting — поддержка трудоустройства моряков',
        description:
          'Профессиональная поддержка в трудоустройстве, скрининг профиля и консультации по документации для моряков.',
      },
      companies: {
        title:
          'ELVORA Consulting — подбор экипажей для судов и офшорных проектов',
        description:
          'Профессиональный рекрутинг, скрининг и управление экипажами для судовладельцев и операторов.',
      },
    },
    header: {
      forCompanies: 'Для Компаний',
      forSeafarers: 'Для Моряков',
      sendCV: 'Отправить резюме',
      menu: 'Меню',
    },
    hero: {
      title:
        'ELVORA Consulting — операционная и техническая поддержка морских и офшорных проектов',
      description:
        'Помогаем судовладельцам, операторам и техническим командам: координация работ, документация и соответствие требованиям (compliance), аналитика и срочные запросы.',
      discussTask: 'Обсудить задачу',
      submitProfile: 'Отправить резюме',
      tagline:
        'Работаем на долгосрочное сотрудничество с компаниями и специалистами, а не на разовые заявки.',
    },
    services: {
      pill: 'О Нас',
      title: 'Как мы поддерживаем морские\nи офшорные операции',
      desc1:
        'Мы поддерживаем компании и операторов, решая операционные, технические и организационные задачи.',
      desc2:
        'От документации и координации до сложных, нестандартных запросов — ELVORA предоставляет четкие и практичные решения',
      items: {
        projectSupport: {
          title: 'Проектная и операционная поддержка',
          desc: 'Комплексная помощь для текущих операций и проектов',
        },
        documentation: {
          title: 'Документация и соответствие требованиям (Compliance)',
          desc: 'Структурирование документов, требований и согласований',
        },
        technical: {
          title: 'Техническая и аналитическая поддержка',
          desc: 'Анализ, отчётность и помощь в принятии решений',
        },
        coordination: {
          title: 'Координация и управление процессами',
          desc: 'Чёткая коммуникация и контроль между всеми сторонами',
        },
        nonStandard: {
          title: 'Нестандартные и срочные задачи',
          desc: 'Быстрая реакция на сложные и срочные запросы',
        },
      },
    },
    clients: {
      pill: 'Услуги',
      title: 'Услуги для компаний\nи моряков',
      desc1:
        'Мы выполняем операционные, технические и организационные задачи для морских и промышленных проектов.',
      desc2:
        'От документации и координации до сложных, нестандартных запросов — мы фокусируемся на решениях, а не ярлыках.',
      tabCompanies: 'Для Компаний',
      tabSpecialists: 'Для Моряков',
      learnMoreSpecialists: 'Узнать Больше О Специалистах',
      learnMoreCompanies: 'Узнать Больше О Компаниях',
      specialistItems: [
        {
          id: '01',
          title: 'Подача и Просмотр Профиля',
          desc: 'Мы просматриваем профессиональные профили и документацию, чтобы подобрать специалистов к соответствующим задачам и проектам',
        },
        {
          id: '02',
          title: 'Консультации по Документации',
          desc: 'Поддержка в понимании требований, структуры документации и ожиданий по соответствию',
        },
        {
          id: '03',
          title: 'Координация Проектов',
          desc: 'Четкая коммуникация в течение процесса вовлечения в задачи или проекты',
        },
        {
          id: '04',
          title: 'Профессиональная Поддержка',
          desc: 'Руководство и помощь во время сотрудничества с компаниями и проектными командами',
        },
        {
          id: '05',
          title: 'Долгосрочные Возможности',
          desc: 'Построение структурированного долгосрочного профессионального сотрудничества на основе опыта и надежности',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'Проектная и Операционная Поддержка',
          desc: 'Мы поддерживаем текущие операции и проекты, беря на себя ответственность за конкретные задачи, координацию и выполнение',
        },
        {
          id: '02',
          title: 'Документация и Соответствие',
          desc: 'Структурированная обработка документации, требований и утверждений в соответствии с проектными и регуляторными потребностями',
        },
        {
          id: '03',
          title: 'Координация и Управление Процессами',
          desc: 'Четкая коммуникация и контроль между отделами, подрядчиками и вовлеченными сторонами',
        },
        {
          id: '04',
          title: 'Техническая и Аналитическая Поддержка',
          desc: 'Анализ, отчетность и структурированная информация для поддержки операционных и управленческих решений',
        },
        {
          id: '05',
          title: 'Нестандартные и Срочные Задачи',
          desc: 'Быстрая реакция на сложные или срочные запросы, требующие опыта и внимания',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'Частые вопросы',
      desc: 'Для быстрых вопросов или уточнений вы можете связаться с нами через мессенджер.',
      items: [
        {
          id: '01',
          q: 'Какие типы задач вы выполняете?',
          a: 'Мы работаем с операционными, техническими, организационными и документальными задачами в морских, энергетических и промышленных проектах.',
        },
        {
          id: '02',
          q: 'Кто может обратиться в ELVORA Consulting?',
          a: 'Судовладельцы, технические менеджеры, операторы и руководители промышленных проектов могут обращаться к нам за поддержкой в решении операционных задач.',
        },
        {
          id: '03',
          q: 'Вы работаете только с офшорными проектами?',
          a: 'Нет, мы работаем как с офшорными, так и с береговыми проектами в морском, энергетическом и промышленном секторах.',
        },
        {
          id: '04',
          q: 'Как вы начинаете работу над запросом?',
          a: 'Мы начинаем с короткой консультации, чтобы понять ваши потребности, после чего предоставляем четкое предложение со сроками и результатами.',
        },
        {
          id: '05',
          q: 'Является ли информация конфиденциальной?',
          a: 'Да, вся информация обрабатывается со строгой конфиденциальностью. Мы придерживаемся протоколов NDA и стандартов защиты данных.',
        },
      ],
    },
    jobs: {
      pill: 'Вакансии',
      sendResume: 'Отправить резюме',
      items: [
        {
          title: 'Морской инженер',
          type: 'Нефтяной танкер / VLCC',
          desc: 'Опытный морской инженер для работы на VLCC. Ответственность за обслуживание главного двигателя, устранение неисправностей и обеспечение соблюдения правил безопасности.',
        },
        {
          title: 'Старший помощник капитана',
          type: 'Контейнеровоз',
          desc: 'Ищем квалифицированного старшего помощника для контейнеровоза. Обязанности включают грузовые операции, навигацию, управление безопасностью и надзор за экипажем.',
        },
        {
          title: 'Инженер-электрик',
          type: 'Офшорная платформа',
          desc: 'Позиция инженера-электрика на офшорной платформе. Обслуживание и ремонт электрических систем, оборудования для генерации электроэнергии и систем управления.',
        },
        {
          title: 'Палубный кадет',
          type: 'Балкер',
          desc: 'Начальная позиция для палубного кадета на балкере. Возможность обучения для выпускников морских академий.',
        },
        {
          title: 'Второй механик',
          type: 'Газовоз LNG',
          desc: 'Требуется второй механик для работы на LNG-газовозе. Управление операциями в машинном отделении, графиками технического обслуживания и технической документацией.',
        },
        {
          title: 'Боцман',
          type: 'Круизное судно',
          desc: 'Опытный боцман для работы на круизном лайнере. Руководство палубной командой, координация работ по техническому обслуживанию.',
        },
        {
          title: 'Морской суперинтендант',
          type: 'Управление флотом',
          desc: 'Береговая позиция морского суперинтенданта. Надзор за техническими операциями флота, проведение инспекций.',
        },
        {
          title: 'Менеджер QHSE',
          type: 'Офшорная установка',
          desc: 'Менеджер по качеству, охране здоровья, безопасности и экологии для офшорных операций. Разработка и внедрение политики QHSE.',
        },
        {
          title: 'Матрос 1 класса',
          type: 'Генеральный груз',
          desc: 'Позиция матроса первого класса на судне генеральных грузов. Выполнение палубных работ, грузовых операций и несение вахты.',
        },
        {
          title: 'Старший механик',
          type: 'Химовоз',
          desc: 'Позиция старшего механика для химовоза. Общая ответственность за машинное отделение и планирование технического обслуживания.',
        },
        {
          title: 'Радиооператор',
          type: 'Исследовательское судно',
          desc: 'Требуется радиооператор для научно-исследовательского судна. Обслуживание систем связи и поддержка исследовательских операций.',
        },
        {
          title: 'Пампмен',
          type: 'Продуктовоз',
          desc: 'Опытный пампмен для продуктовоза. Ответственность за грузовые насосные системы и мойку танков.',
        },
        {
          title: 'Оператор DP',
          type: 'Судно обеспечения (OSV)',
          desc: 'Оператор динамического позиционирования для OSV. Управление системами DP во время офшорных операций.',
        },
        {
          title: 'Крановщик',
          type: 'Тяжеловозное судно',
          desc: 'Сертифицированный крановщик для судна большой грузоподъемности. Управление палубными кранами для грузовых операций.',
        },
        {
          title: 'Менеджер по кейтерингу',
          type: 'Пассажирский паром',
          desc: 'Менеджер по кейтерингу для пассажирского парома. Управление работой камбуза, обслуживанием питания и инвентаризацией.',
        },
      ],
    },
    contact: {
      title: 'Свяжитесь с нами',
      desc: 'Расскажите о вашей задаче, вызове или проекте. Мы рассмотрим запрос и вернемся с четким и практичным решением',
      labels: {
        company: 'Название компании',
        person: 'Контактное лицо',
        email: 'Электронная почта',
        phone: 'Телефон / WhatsApp',
        task: 'Опишите вашу задачу или запрос',
        project: 'Тип проекта / судна',
        files: 'Прикрепить файлы',
        dragDrop: 'Перетащите файлы сюда или нажмите для выбора',
        fileTypes:
          'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (Макс. 48 МБ)',
        phoneHint: 'Укажите код страны (например, +1, +44, +380)',
      },
      button: 'Отправить Запрос',
      disclaimer:
        'Вся информация является конфиденциальной. Мы не передаем ваши данные третьим сторонам',
    },
    footer: {
      discussTask: 'Обсудить задачу',
      sendProfile: 'Отправить резюме',
      privacy: 'Политика Конфиденциальности',
      terms: 'Условия Использования',
    },
    specialists: {
      hero: {
        title: 'Поддержка Трудоустройства Моряков',
        desc: 'Этот раздел посвящен морякам, ищущим профессиональную поддержку в трудоустройстве, пересмотр профиля и доступ к карьерным возможностям у судовладельцев и операторов морского и оффшорного секторов.',
        submitCV: 'Отправить резюме',
        viewOpportunities: 'Просмотреть Вакансии',
        tagline1:
          'Связь с проверенными судовладельцами и операторами через профессиональную поддержку.',
        tagline2:
          'Отправьте свой профиль для структурированного пересмотра, консультаций по документации и рассмотрения возможностей.',
      },
      benefits: {
        pill: 'Поддержка',
        title: 'Профессиональная Поддержка\nТрудоустройства Моряков',
        desc1:
          'Мы предоставляем структурированную поддержку в трудоустройстве, скрининг профиля и консультации по документации для моряков, ищущих работу у проверенных морских и оффшорных операторов.',
        desc2:
          'Отправьте свой профиль для структурированного пересмотра, консультаций по документации и рассмотрения возможностей.',
        items: {
          profile: {
            title: 'Пересмотр и Скрининг Профиля',
            desc: 'Профили моряков пересматриваются для оценки ранга, опыта на судах и готовности документов перед рассмотрением для текущих или будущих возможностей.',
          },
          docs: {
            title: 'Консультации по Документации',
            desc: 'Консультации по необходимым сертификатам, подтверждениям и документации, требуемой судовладельцами и оффшорными операторами.',
          },
          matching: {
            title: 'Поддержка в Подборе Вакансий',
            desc: 'Профессиональная поддержка в соответствии профилей моряков запросам на найм на основе опыта, доступности и требований проекта.',
          },
          access: {
            title: 'Доступ к Проверенным Возможностям',
            desc: 'Рассмотрение для морских и оффшорных возможностей у судовладельцев и операторов при условии пересмотра профиля и соответствия требованиям клиента.',
          },
        },
      },
      steps: {
        pill: 'Процесс',
        title: 'Как Это Работает',
        desc: 'Ваш путь от заявки до трудоустройства',
        items: [
          {
            title: 'Подача Профиля',
            desc: 'Отправьте свое CV, ранг, опыт на судах и доступность для профессионального пересмотра и рассмотрения.',
          },
          {
            title: 'Пересмотр и Скрининг',
            desc: 'Мы проверяем ваши документы и оцениваем соответствие для текущих и будущих запросов на найм.',
          },
          {
            title: 'Рассмотрение для Трудоустройства',
            desc: 'Квалифицированные профили подбираются под требования судовладельцев и операторов на основе опыта.',
          },
          {
            title: 'Прямое Взаимодействие с Клиентом',
            desc: 'Если ваша кандидатура отобрана, вы общаетесь непосредственно с компанией-работодателем для согласования условий трудоустройства.',
          },
        ],
      },
      requirements: {
        pill: 'Документы',
        title: 'Необходимые Документы для Трудоустройства',
        desc: 'Стандартные документы, обычно требуемые в процессе найма и пересмотра профиля моряка',
        items: [
          "Паспорт моряка (Seafarer's Book)",
          'Загранпаспорт (действительный)',
          'Рабочий диплом (CoC)',
          'Сертификаты STCW',
          'Медицинский сертификат (ENG1 / эквивалент)',
          'BOSIET / Оффшорные сертификаты безопасности (если применимо)',
          'Сертификат DP (для позиций DP, если применимо)',
          'CV / Послужная книжка',
        ],
        note: 'Точный перечень необходимых документов может отличаться в зависимости от типа судна, требований проекта, государства флага и политики клиента.',
      },
      faq: {
        pill: 'FAQ',
        title: 'Частые Вопросы',
        items: [
          {
            q: 'Гарантируете ли вы трудоустройство после подачи CV?',
            a: 'Нет. Подача профиля не гарантирует трудоустройство. Все профили рассматриваются индивидуально и могут быть учтены для запросов на найм на основе опыта, доступности и требований клиента.',
          },
          {
            q: 'Вы действуете как крюинговое агентство?',
            a: 'Нет. Elvora предоставляет поддержку в трудоустройстве и консультационные услуги. Решения о найме и контракты заключаются непосредственно между моряками и компаниями-работодателями.',
          },
          {
            q: 'Взимаете ли вы плату с моряков?',
            a: 'Мы не взимаем плату с моряков за подачу профилей. Некоторые дополнительные услуги, такие как консультации по документации или карьерный консалтинг, могут предлагаться отдельно по запросу.',
          },
          {
            q: 'С какими типами судов и проектов вы работаете?',
            a: 'Мы поддерживаем запросы на найм в морском и оффшорном секторах, включая торговые суда, судна оффшорного флота и проектные операции.',
          },
          {
            q: 'Сколько времени занимает процесс рассмотрения?',
            a: 'Пересмотр профиля зависит от текущей активности найма и запросов клиентов. Связываются только с отобранными кандидатами.',
          },
          {
            q: 'Как со мной свяжутся, если мой профиль подходит?',
            a: 'С отобранными кандидатами связываются напрямую, используя контактные данные, предоставленные при подаче профиля.',
          },
        ],
      },
      form: {
        pill: 'Заявка',
        title: 'Отправить Профиль Моряка',
        desc1:
          'Отправьте свой профессиональный профиль для пересмотра в рамках процесса поддержки трудоустройства.',
        desc2: 'Подача не гарантирует трудоустройство.',
        labels: {
          firstName: 'Имя',
          lastName: 'Фамилия',
          rank: 'Ранг / Позиция',
          selectRank: 'Выберите ранг/позицию',
          specify: 'Пожалуйста, уточните',
          enterPosition: 'Введите вашу позицию',
          email: 'Email',
          phone: 'Телефон / WhatsApp',
          phoneHint: 'Укажите код страны (например, +1, +44, +380)',
          cv: 'Загрузка CV (PDF, DOC или Изображение)',
          dragDrop: 'Перетащите ваше CV сюда или нажмите для выбора',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (Макс. 48 МБ)',
          vesselType: 'Опыт по Типу Судна',
          selectVessel: 'Выберите тип судна',
          yearsExp: 'Лет Опыта',
          availability: 'Дата Готовности',
          availabilityHint: 'Выберите самую раннюю дату готовности',
          consent:
            'Я подтверждаю, что предоставленная информация является точной, и соглашаюсь на обработку моих персональных данных для целей поддержки трудоустройства.',
          submit: 'Отправить Профиль',
          disclaimer:
            'Вся информация является конфиденциальной. Связываются только с отобранными кандидатами.',
        },
        ranks: {
          deck: 'Палубная Команда',
          engine: 'Машинная Команда',
          other: 'Другое',
          options: {
            master: 'Капитан (Master)',
            chiefMate: 'Старший Помощник (Chief Mate)',
            secondMate: 'Второй Помощник (Second Mate)',
            thirdMate: 'Третий Помощник (Third Mate)',
            bosun: 'Боцман (Bosun)',
            ab: 'Матрос 1 класса (AB)',
            os: 'Матрос 2 класса (OS)',
            chiefEng: 'Старший Механик (Chief Engineer)',
            secondEng: 'Второй Механик (Second Engineer)',
            thirdEng: 'Третий Механик (Third Engineer)',
            fourthEng: 'Четвертый Механик (Fourth Engineer)',
            eto: 'Электромеханик (ETO)',
            asstEto: 'Ассистент Электромеханика (Assistant ETO)',
            motorman: 'Моторист (Motorman / Oiler)',
            wiper: 'Вайпер (Wiper)',
            dpo: 'Оператор DP (DPO)',
            sdpo: 'Старший Оператор DP (SDPO)',
            craneOp: 'Крановщик (Crane Operator)',
            rigger: 'Стропальщик (Rigger)',
            electrician: 'Электрик (Electrician)',
            mechanic: 'Механик (Mechanic)',
            other: 'Другое (пожалуйста, уточните)',
          },
        },
        vessels: {
          tanker: 'Танкер',
          bulk: 'Балкер',
          container: 'Контейнеровоз',
          offshore: 'Судно Оффшорного Флота (OSV)',
          cruise: 'Круизное Судно',
          other: 'Другое',
        },
      },
      contact: {
        pill: 'Контакты',
        title: 'Нужна Помощь?',
        desc: 'Свяжитесь с нами через мессенджер или email для быстрой помощи',
      },
    },
    companies: {
      hero: {
        title: 'Надежное Снабжение Экипажами для Судов и Оффшорных Проектов',
        desc1:
          'Профессиональный рекрутинг, скрининг и управление экипажами для судовладельцев и операторов.',
        desc2: 'От отдельных позиций до полных экипажей.',
        requestCrew: 'Запрос на Экипаж',
        discussTask: 'Обсудить Задачу',
      },
      services: {
        pill: 'Услуги',
        title: 'Услуги для Судовладельцев\nи Операторов',
        desc1:
          'Комплексные решения по подбору и управлению экипажем для морских и оффшорных операций.',
        desc2:
          'От отбора кандидатов до полного соответствия требованиям и логистики экипажа.',
        items: {
          recruitment: {
            title: 'Подбор Экипажа',
            desc: 'Полный цикл рекрутинга для всех рангов и типов судов — оффшорный и торговый флот.',
          },
          screening: {
            title: 'Скрининг и Проверка',
            desc: 'Проверка документов, биографии, подтверждение опыта и скрининг на соответствие требованиям.',
          },
          compliance: {
            title: 'Управление Соответствием',
            desc: 'Полное покрытие требований STCW, MLC, государства флага и специфических требований клиента.',
          },
          coordination: {
            title: 'Координация Экипажа',
            desc: 'Смены экипажа, организация путешествий, мобилизация и планирование замен.',
          },
        },
      },
      steps: {
        pill: 'Процесс',
        title: 'Как Мы Работаем',
        desc: 'Четкий и прозрачный процесс подбора экипажа — от запроса до посадки на судно',
        items: [
          {
            title: 'Запрос',
            desc: 'Отправьте ваши требования к экипажу и детали судна или проекта',
          },
          {
            title: 'Скрининг',
            desc: 'Отбор кандидатов, проверка документов и соответствия требованиям',
          },
          {
            title: 'Подбор и Интервью',
            desc: 'Формирование короткого списка квалифицированных кандидатов и организация интервью',
          },
          {
            title: 'Мобилизация',
            desc: 'Подписание контракта, мобилизация экипажа и текущая координация',
          },
        ],
      },
      compliance: {
        pill: 'Соответствие',
        title: 'Соответствие и Конфиденциальность',
        desc: 'Мы работаем в полном соответствии с международными морскими стандартами и обеспечиваем строгую защиту данных',
        items: {
          nda: {
            title: 'NDA и Защита Данных',
            desc: 'Вся информация клиентов и экипажа обрабатывается с соблюдением строгой конфиденциальности и принципов неразглашения.',
          },
          verification: {
            title: 'Проверка Документов',
            desc: 'Тщательная проверка сертификатов, лицензий, медицинских справок и опыта перед подачей.',
          },
          stcw: {
            title: 'Соответствие STCW и MLC',
            desc: 'Полное соответствие требованиям STCW, MLC 2006, государства флага и специфических требований клиента.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'Частые Вопросы',
        items: [
          {
            q: 'Вы действуете как работодатель или крюинговое агентство?',
            a: 'Нет. Elvora предоставляет услуги по рекрутингу и операционной поддержке. Трудовые договоры подписываются непосредственно между экипажем и компанией-работодателем.',
          },
          {
            q: 'Какие типы судов и проектов вы поддерживаете?',
            a: 'Мы поддерживаем торговые суда и оффшорные проекты, включая танкеры, судна оффшорного флота и проектные морские операции.',
          },
          {
            q: 'Вы предоставляете отдельных членов экипажа или полные экипажи?',
            a: 'Мы поддерживаем как запросы на отдельные позиции, так и решения для полных или частичных экипажей, в зависимости от объема проекта и требований клиента.',
          },
          {
            q: 'Как вы обеспечиваете качество кандидатов и соответствие требованиям?',
            a: 'Все кандидаты проходят проверку документов, обзор опыта и проверку на соответствие стандартам STCW, MLC, государства флага и клиента.',
          },
          {
            q: 'Как быстро может быть мобилизован экипаж?',
            a: 'Сроки мобилизации зависят от типа судна, позиции, доступности и статуса документов. Сроки обсуждаются индивидуально для каждого запроса.',
          },
          {
            q: 'Вы работаете по всему миру?',
            a: 'Да. Мы поддерживаем международные проекты и сотрудничаем с судовладельцами и операторами в разных регионах.',
          },
        ],
      },
      form: {
        pill: 'Запрос',
        title: 'Запрос на Экипаж или Обсуждение Проекта',
        desc1: 'Отправьте ваши требования к экипажу или детали проекта.',
        desc2:
          'Мы рассматриваем каждый запрос индивидуально и предоставляем ответ.',
        sections: {
          company: 'Информация о Компании',
          details: 'Детали Запроса',
          timeline: 'Сроки',
        },
        labels: {
          companyName: 'Название Компании',
          contactPerson: 'Контактное Лицо',
          email: 'Email',
          phone: 'Телефон / WhatsApp',
          vesselType: 'Тип Судна / Проекта',
          selectVessel: 'Выберите тип судна/проекта',
          positions: 'Необходимые Позиции',
          selectPositions: 'Выберите позиции',
          crewNumber: 'Количество Экипажа',
          location: 'Локация / Регион',
          selectLocation: 'Выберите регион (опционально)',
          additional: 'Дополнительные Требования',
          additionalPlaceholder:
            'Специфические сертификаты, требования к опыту, детали проекта...',
          startDate: 'Желаемая Дата Начала',
          duration: 'Длительность Контракта',
          selectDuration: 'Выберите длительность (опционально)',
          consent:
            'Я подтверждаю, что предоставленная информация является точной, и соглашаюсь на обработку данных для целей рекрутинга и операционной поддержки.',
          submit: 'Отправить Запрос',
          disclaimer:
            'Вся информация обрабатывается конфиденциально согласно NDA. Мы отвечаем в течение 24 часов.',
        },
        vesselTypes: [
          'Торговое Судно',
          'Танкер',
          'Судно Оффшорного Флота (OSV)',
          'Jack-Up / Оффшорная Установка',
          'Проектная Операция',
          'Другое',
        ],
        regions: [
          'Весь Мир',
          'Европа',
          'Ближний Восток',
          'Африка',
          'Азиатско-Тихоокеанский регион',
          'Другое',
        ],
        durations: {
          m1: '1 месяц',
          m2: '2 месяца',
          m3: '3 месяца',
          m4: '4 месяца',
          m6: '6 месяцев',
          m9: '9 месяцев',
          m12: '12 месяцев',
          ongoing: 'Постоянная / Бессрочная',
          project: 'Проектная',
        },
      },
      contact: {
        pill: 'Контакты',
        title: 'Свяжитесь с Нами',
        desc: 'Свяжитесь с нами для запросов на экипаж, консультаций или возможностей партнерства',
      },
    },
  },
  ka: {
    seo: {
      home: {
        title: 'ELVORA Consulting — საზღვაო და ოფშორული პროექტების მხარდაჭერა',
        description:
          'ELVORA Consulting: კოორდინაცია, დოკუმენტაცია, შესაბამისობა (compliance) და ტექნიკური მხარდაჭერა საზღვაო/ოფშორული პროექტებისთვის.',
      },
      specialists: {
        title: 'ELVORA Consulting — მეზღვაურთა დასაქმების მხარდაჭერა',
        description:
          'პროფესიული მხარდაჭერა დასაქმებაში, პროფილის სკრინინგი და დოკუმენტაციის კონსულტაცია მეზღვაურებისთვის.',
      },
      companies: {
        title:
          'ELVORA Consulting — ეკიპაჟების მიწოდება გემებისა და ოფშორული პროექტებისთვის',
        description:
          'ეკიპაჟის პროფესიული რეკრუტინგი, სკრინინგი და მართვა გემთმფლობელებისა და ოპერატორებისთვის.',
      },
    },
    header: {
      forCompanies: 'კომპანიებისთვის',
      forSeafarers: 'მეზღვაურებისთვის',
      sendCV: 'CV გაგზავნა',
      menu: 'მენიუ',
    },
    hero: {
      title:
        'ELVORA Consulting — საზღვაო და ოფშორული პროექტების ოპერაციული და ტექნიკური მხარდაჭერა',
      description:
        'ვეხმარებით გემთმფლობელებს, ოპერატორებს და ტექნიკურ გუნდებს: კოორდინაცია, დოკუმენტაცია და შესაბამისობა (compliance), ანალიტიკა და გადაუდებელი მოთხოვნები.',
      discussTask: 'მოთხოვნის განხილვა',
      submitProfile: 'CV გაგზავნა',
      tagline:
        'ვმუშაობთ გრძელვადიან თანამშრომლობაზე კომპანიებთან და სპეციალისტებთან, და არა ერთჯერად მოთხოვნებზე.',
    },
    services: {
      pill: 'ჩვენს შესახებ',
      title: 'როგორ ვუჭერთ მხარს საზღვაო\nდა ოფშორულ ოპერაციებს',
      desc1:
        'ჩვენ მხარს ვუჭერთ კომპანიებსა და ოპერატორებს ოპერაციული, ტექნიკური და ორგანიზაციული ამოცანების გადაწყვეტილებაში.',
      desc2:
        'დოკუმენტაციიდან და კოორდინაციიდან რთულ, არასტანდარტულ მოთხოვნებამდე — ELVORA გთავაზობთ მკაფიო და პრაქტიკულ გადაწყვეტილებებს',
      items: {
        projectSupport: {
          title: 'პროექტისა და ოპერაციების მხარდაჭერა',
          desc: 'მიმდინარე ოპერაციებისა და პროექტების პრაქტიკული მხარდაჭერა',
        },
        documentation: {
          title: 'დოკუმენტაცია და შესაბამისობა (Compliance)',
          desc: 'დოკუმენტების, მოთხოვნებისა და დამტკიცებების სტრუქტურირება',
        },
        technical: {
          title: 'ტექნიკური და ანალიტიკური მხარდაჭერა',
          desc: 'ანალიზი, რეპორტინგი და გადაწყვეტილების მხარდაჭერა',
        },
        coordination: {
          title: 'პროცესების კოორდინაცია და მართვა',
          desc: 'კომუნიკაცია და კონტროლი ყველა მხარეს შორის',
        },
        nonStandard: {
          title: 'არასტანდარტული და გადაუდებელი მოთხოვნები',
          desc: 'სწრაფი რეაგირება რთულ და გადაუდებელ ამოცანებზე',
        },
      },
    },
    clients: {
      pill: 'სერვისები',
      title: 'სერვისები კომპანიებისა\nდა მეზღვაურებისთვის',
      desc1:
        'ჩვენ ვასრულებთ ოპერაციულ, ტექნიკურ და ორგანიზაციულ ამოცანებს საზღვაო და ინდუსტრიული პროექტებისთვის.',
      desc2:
        'დოკუმენტაციიდან და კოორდინაციიდან რთულ, არასტანდარტულ მოთხოვნებამდე — ჩვენ ვფოკუსირდებით გადაწყვეტილებებზე.',
      tabCompanies: 'კომპანიებისთვის',
      tabSpecialists: 'მეზღვაურებისთვის',
      learnMoreSpecialists: 'გაიგეთ მეტი სპეციალისტების შესახებ',
      learnMoreCompanies: 'გაიგეთ მეტი კომპანიების შესახებ',
      specialistItems: [
        {
          id: '01',
          title: 'პროფილის წარდგენა და განხილვა',
          desc: 'ჩვენ განვიხილავთ პროფესიულ პროფილებს და დოკუმენტაციას სპეციალისტების შესაბამის ამოცანებსა და პროექტებთან დასაკავშირებლად',
        },
        {
          id: '02',
          title: 'კონსულტაცია დოკუმენტაციაზე',
          desc: 'მხარდაჭერა მოთხოვნების, დოკუმენტაციის სტრუქტურისა და შესაბამისობის გაგებაში',
        },
        {
          id: '03',
          title: 'პროექტის კოორდინაცია',
          desc: 'მკაფიო კომუნიკაცია დავალებაში ან პროექტში ჩართულობის მთელი პროცესის განმავლობაში',
        },
        {
          id: '04',
          title: 'პროფესიული მხარდაჭერა',
          desc: 'ხელმძღვანელობა და დახმარება კომპანიებთან და საპროექტო გუნდებთან თანამშრომლობისას',
        },
        {
          id: '05',
          title: 'გრძელვადიანი შესაძლებლობები',
          desc: 'სტრუქტურირებული გრძელვადიანი პროფესიული თანამშრომლობის აგება გამოცდილებასა და საიმედოობაზე დაყრდნობით',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'საპროექტო და ოპერაციული მხარდაჭერა',
          desc: 'ჩვენ მხარს ვუჭერთ მიმდინარე ოპერაციებსა და პროექტებს კონკრეტულ ამოცანებზე, კოორდინაციასა და შესრულებაზე პასუხისმგებლობით',
        },
        {
          id: '02',
          title: 'დოკუმენტაცია და შესაბამისობა',
          desc: 'დოკუმენტაციის, მოთხოვნებისა და დამტკიცებების სტრუქტურირებული დამუშავება პროექტისა და რეგულაციების შესაბამისად',
        },
        {
          id: '03',
          title: 'კოორდინაცია და პროცესების მართვა',
          desc: 'მკაფიო კომუნიკაცია და კონტროლი დეპარტამენტებს, კონტრაქტორებსა და ჩართულ მხარეებს შორის',
        },
        {
          id: '04',
          title: 'ტექნიკური და ანალიტიკური მხარდაჭერა',
          desc: 'ანალიზი, ანგარიშგება და სტრუქტურირებული ინფორმაცია ოპერაციული და მმართველობითი გადაწყვეტილების მხარდასაჭერად',
        },
        {
          id: '05',
          title: 'არასტანდარტული და სასწრაფო ამოცანები',
          desc: 'სწრაფი რეაგირება რთულ ან დროში შეზღუდულ მოთხოვნებზე, რომლებიც გამოცდილებასა და ყურადღებას მოითხოვს',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'ხშირად დასმული კითხვები',
      desc: 'სწრაფი კითხვებისთვის ან დაზუსტებისთვის შეგიძლიათ დაგვიკავშირდეთ მესენჯერის საშუალებით.',
      items: [
        {
          id: '01',
          q: 'რა ტიპის ამოცანებს ასრულებთ?',
          a: 'ჩვენ ვმუშაობთ ოპერაციულ, ტექნიკულ, ორგანიზაციულ და დოკუმენტაციასთან დაკავშირებულ ამოცანებზე საზღვაო, ენერგეტიკულ და ინდუსტრიულ პროექტებში.',
        },
        {
          id: '02',
          q: 'ვის შეუძლია დაუკავშირდეს ELVORA-ს?',
          a: 'გემთმფლობელებს, ტექნიკურ მენეჯერებს, ოპერატორებს და ინდუსტრიული პროექტების ხელმძღვანელებს შეუძლიათ დაგვიკავშირდნენ ოპერაციული გამოწვევების მხარდაჭერისთვის.',
        },
        {
          id: '03',
          q: 'მხოლოდ ოფშორულ პროექტებზე მუშაობთ?',
          a: 'არა, ჩვენ ვმუშაობთ როგორც ოფშორულ, ისე სანაპირო პროექტებზე საზღვაო, ენერგეტიკულ და ინდუსტრიულ სექტორებში.',
        },
        {
          id: '04',
          q: 'როგორ იწყებთ მოთხოვნაზე მუშაობას?',
          a: 'ჩვენ ვიწყებთ მოკლე კონსულტაციით თქვენი საჭიროებების გასაგებად, რის შემდეგაც გთავაზობთ მკაფიო წინადადებას ვადებითა და შედეგებით.',
        },
        {
          id: '05',
          q: 'არის თუ არა ინფორმაცია კონფიდენციალური?',
          a: 'დიახ, ყველა ინფორმაცია მუშავდება მკაცრი კონფიდენციალურობით. ჩვენ ვიცავთ NDA პროტოკოლებსა და მონაცემთა დაცვის სტანდარტებს.',
        },
      ],
    },
    jobs: {
      pill: 'ვაკანსიები',
      sendResume: 'CV გაგზავნა',
      items: [
        {
          title: 'საზღვაო ინჟინერი',
          type: 'ნავთობის ტანკერი / VLCC',
          desc: 'გამოცდილი საზღვაო ინჟინერი VLCC ოპერაციებისთვის. პასუხისმგებლობა მთავარი ძრავის ტექნიკურ მომსახურებაზე, გაუმართაობის აღმოფხვრასა და უსაფრთხოების წესების დაცვაზე.',
        },
        {
          title: 'უფროსი ოფიცერი',
          type: 'კონტეინერული გემი',
          desc: 'ვეძებთ კვალიფიციურ უფროს თანაშემწეს საკონტეინერო გემისთვის. მოვალეობებში შედის ტვირთის ოპერაციები, ნავიგაცია, უსაფრთხოების მართვა და ეკიპაჟის ზედამხედველობა.',
        },
        {
          title: 'ელექტროინჟინერი',
          type: 'ოფშორული პლატფორმა',
          desc: 'ელექტროინჟინრის პოზიცია ოფშორულ პლატფორმაზე. ელექტრო სისტემების, ელექტროენერგიის გამომუშავების აღჭურვილობისა და მართვის სისტემების მოვლა და შეკეთება.',
        },
        {
          title: 'გემბანის კადეტი',
          type: 'ბალკერი',
          desc: 'საწყისი პოზიცია გემბანის კადეტისთვის ბალკერზე. ტრენინგის შესაძლებლობა საზღვაო აკადემიის კურსდამთავრებულებისთვის.',
        },
        {
          title: 'მეორე მექანიკოსი',
          type: 'LNG გადამზიდი',
          desc: 'საჭიროა მეორე მექანიკოსი LNG გადამზიდისთვის. სამანქანო განყოფილების ოპერაციების, ტექნიკური მომსახურების გრაფიკებისა და ტექნიკური დოკუმენტაციის მართვა.',
        },
        {
          title: 'ბოცმანი',
          type: 'საკრუიზო გემი',
          desc: 'გამოცდილი ბოცმანი ლუქს კლასის საკრუიზო ოპერაციებისთვის. გემბანის ეკიპაჟის ხელმძღვანელობა, ტექნიკური მომსახურების სამუშაოების კოორდინაცია.',
        },
        {
          title: 'საზღვაო სუპერინტენდანტი',
          type: 'ფლოტის მართვა',
          desc: 'საზღვაო სუპერინტენდანტის სანაპირო პოზიცია. ფლოტის ტექნიკური ოპერაციების ზედამხედველობა, ინსპექტირების ჩატარება.',
        },
        {
          title: 'QHSE მენეჯერი',
          type: 'ოფშორული დანადგარი',
          desc: 'ხარისხის, ჯანმრთელობის, უსაფრთხოებისა და გარემოს დაცვის მენეჯერი ოფშორული ოპერაციებისთვის. QHSE პოლიტიკის შემუშავება და დანერგვა.',
        },
        {
          title: 'პირველი კლასის მატროსი',
          type: 'გენერალური ტვირთი',
          desc: 'პირველი კლასის მატროსის პოზიცია გენერალური ტვირთების გემზე. გემბანის ტექნიკური მომსახურება, სატვირთო ოპერაციები და ვახტის დგომა.',
        },
        {
          title: 'უფროსი მექანიკოსი',
          type: 'ქიმიური ტანკერი',
          desc: 'უფროსი მექანიკოსის პოზიცია ქიმიურ ტანკერზე. საერთო პასუხისმგებლობა სამანქანო განყოფილებასა და ტექნიკური მომსახურების დაგეგმვაზე.',
        },
        {
          title: 'რადიოოპერატორი',
          type: 'კვლევითი გემი',
          desc: 'საჭიროა რადიოოპერატორი სამეცნიერო-კვლევითი გემისთვის. საკომუნიკაციო სისტემების მომსახურება და კვლევითი ოპერაციების მხარდაჭერა.',
        },
        {
          title: 'პამპმენი',
          type: 'პროდუქტმზიდი',
          desc: 'გამოცდილი პამპმენი პროდუქტმზიდისთვის. პასუხისმგებლობა სატვირთო სატუმბ სისტემებსა და ტანკების რეცხვაზე.',
        },
        {
          title: 'DP ოპერატორი',
          type: 'ოფშორული მხარდაჭერის გემი (OSV)',
          desc: 'დინამიური პოზიციონირების ოპერატორი OSV-სთვის. DP სისტემების მართვა ოფშორული ოპერაციების დროს.',
        },
        {
          title: 'ამწის ოპერატორი',
          type: 'მძიმე ტვირთმზიდი გემი',
          desc: 'სერტიფიცირებული ამწის ოპერატორი მძიმე ტვირთმზიდი გემისთვის. გემბანის ამწეების მართვა სატვირთო ოპერაციებისთვის.',
        },
        {
          title: 'კვების მენეჯერი',
          type: 'სამგზავრო ბორანი',
          desc: 'კვების მენეჯერი სამგზავრო ბორანისთვის. სამზარეულოს ოპერაციების, კვების სერვისისა და ინვენტარის მართვა.',
        },
      ],
    },
    contact: {
      title: 'დაგვიკავშირდით',
      desc: 'გვიამბეთ თქვენი ამოცანის, გამოწვევის ან პროექტის შესახებ. ჩვენ განვიხილავთ მოთხოვნას და დაგიბრუნდებით მკაფიო და პრაქტიკული გადაწყვეტილებით',
      labels: {
        company: 'კომპანიის სახელი',
        person: 'საკონტაქტო პირი',
        email: 'ელ. ფოსტა',
        phone: 'ტელეფონი / WhatsApp',
        task: 'აღწერეთ თქვენი ამოცანა ან მოთხოვნა',
        project: 'პროექტის / გემის ტიპი',
        files: 'ფაილების მიმაგრება',
        dragDrop: 'გადმოიტანეთ ფაილები აქ ან დააჭირეთ ასარჩევად',
        fileTypes:
          'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (მაქს. 48 MB)',
        phoneHint: 'მიუთითეთ ქვეყნის კოდი (მაგ., +1, +44, +995)',
      },
      button: 'მოთხოვნის გაგზავნა',
      disclaimer:
        'მთელი ინფორმაცია კონფიდენციალურია. ჩვენ არ გადავცემთ თქვენს მონაცემებს მესამე მხარეებს',
    },
    footer: {
      discussTask: 'მოთხოვნის განხილვა',
      sendProfile: 'CV გაგზავნა',
      privacy: 'კონფიდენციალურობის პოლიტიკა',
      terms: 'გამოყენების პირობები',
    },
    specialists: {
      hero: {
        title: 'მეზღვაურთა დასაქმების მხარდაჭერა',
        desc: 'ეს განყოფილება ეძღვნება მეზღვაურებს, რომლებიც ეძებენ პროფესიულ მხარდაჭერას დასაქმებაში, პროფილის განხილვასა და კარიერულ შესაძლებლობებს გემთმფლობელებთან და ოპერატორებთან საზღვაო და ოფშორულ სექტორებში.',
        submitCV: 'CV გაგზავნა',
        viewOpportunities: 'ვაკანსიების ნახვა',
        tagline1:
          'დაუკავშირდით გადამოწმებულ გემთმფლობელებსა და ოპერატორებს პროფესიული დასაქმების მხარდაჭერით.',
        tagline2:
          'გაგზავნეთ თქვენი პროფილი სტრუქტურირებული განხილვისთვის, დოკუმენტაციის კონსულტაციისა და შესაძლებლობების განხილვისთვის.',
      },
      benefits: {
        pill: 'მხარდაჭერა',
        title: 'პროფესიული დასაქმების\nმხარდაჭერა მეზღვაურებისთვის',
        desc1:
          'ჩვენ გთავაზობთ სტრუქტურირებულ მხარდაჭერას დასაქმებაში, პროფილის სკრინინგსა და დოკუმენტაციის კონსულტაციას მეზღვაურებისთვის, რომლებიც ეძებენ სამუშაოს გადამოწმებულ საზღვაო და ოფშორულ ოპერატორებთან.',
        desc2:
          'გაგზავნეთ თქვენი პროფილი სტრუქტურირებული განხილვისთვის, დოკუმენტაციის კონსულტაციისა და შესაძლებლობების განხილვისთვის.',
        items: {
          profile: {
            title: 'პროფილის განხილვა და სკრინინგი',
            desc: 'მეზღვაურთა პროფილები განიხილება რანგის, გემზე მუშაობის გამოცდილებისა და დოკუმენტაციის მზადყოფნის შესაფასებლად მიმდინარე ან მომავალი შესაძლებლობებისთვის.',
          },
          docs: {
            title: 'კონსულტაცია დოკუმენტაციაზე',
            desc: 'კონსულტაცია საჭირო სერტიფიკატებზე, დადასტურებებსა და დოკუმენტაციაზე, რომელსაც ითხოვენ გემთმფლობელები და ოფშორული ოპერატორები.',
          },
          matching: {
            title: 'მხარდაჭერა ვაკანსიების შერჩევაში',
            desc: 'პროფესიული მხარდაჭერა მეზღვაურთა პროფილების შესაბამისობაში მოყვანაში დასაქმების მოთხოვნებთან გამოცდილების, ხელმისაწვდომობისა და პროექტის მოთხოვნების საფუძველზე.',
          },
          access: {
            title: 'წვდომა გადამოწმებულ შესაძლებლობებზე',
            desc: 'განხილვა საზღვაო და ოფშორული შესაძლებლობებისთვის გემთმფლობელებთან და ოპერატორებთან პროფილის განხილვისა და კლიენტის მოთხოვნებთან შესაბამისობის პირობით.',
          },
        },
      },
      steps: {
        pill: 'პროცესი',
        title: 'როგორ მუშაობს',
        desc: 'თქვენი გზა განაცხადიდან დასაქმებამდე',
        items: [
          {
            title: 'პროფილის წარდგენა',
            desc: 'გაგზავნეთ თქვენი CV, რანგი, გემზე მუშაობის გამოცდილება და ხელმისაწვდომობა პროფესიული განხილვისა და განხილვისთვის.',
          },
          {
            title: 'განხილვა და სკრინინგი',
            desc: 'ჩვენ ვამოწმებთ თქვენს დოკუმენტებს და ვაფასებთ შესაბამისობას მიმდინარე და მომავალი დასაქმების მოთხოვნებისთვის.',
          },
          {
            title: 'განხილვა დასაქმებისთვის',
            desc: 'კვალიფიციური პროფილები შეირჩევა გემთმფლობელებისა და ოპერატორების მოთხოვნების შესაბამისად გამოცდილების საფუძველზე.',
          },
          {
            title: 'პირდაპირი ურთიერთობა კლიენტთან',
            desc: 'თუ თქვენი კანდიდატურა შეირჩა, თქვენ პირდაპირ უკავშირდებით დამსაქმებელ კომპანიას დასაქმების პირობების შესათანხმებლად.',
          },
        ],
      },
      requirements: {
        pill: 'დოკუმენტები',
        title: 'საჭირო დოკუმენტები დასაქმებისთვის',
        desc: 'სტანდარტული დოკუმენტები, რომლებიც ჩვეულებრივ მოითხოვება მეზღვაურის დაქირავებისა და პროფილის განხილვის პროცესში',
        items: [
          "მეზღვაურის პასპორტი (Seafarer's Book)",
          'საზღვარგარეთის პასპორტი (მოქმედი)',
          'კომპეტენციის სერტიფიკატი (CoC)',
          'STCW სერტიფიკატები',
          'სამედიცინო სერტიფიკატი (ENG1 / ეკვივალენტი)',
          'BOSIET / ოფშორული უსაფრთხოების სერტიფიკატები (თუ საჭიროა)',
          'DP სერტიფიკატი (DP პოზიციებისთვის, თუ საჭიროა)',
          'CV / ნაოსნობის სტაჟის წიგნაკი',
        ],
        note: 'საჭირო დოკუმენტების ზუსტი სია შეიძლება განსხვავდებოდეს გემის ტიპის, პროექტის მოთხოვნების, დროშის სახელმწიფოსა და კლიენტის პოლიტიკის მიხედვით.',
      },
      faq: {
        pill: 'FAQ',
        title: 'ხშირი კითხვები',
        items: [
          {
            q: 'იძლევით თუ არა დასაქმების გარანტიას CV-ს გაგზავნის შემდეგ?',
            a: 'არა. პროფილის გაგზავნა არ იძლევა დასაქმების გარანტიას. ყველა პროფილი განიხილება ინდივიდუალურად და შეიძლება გათვალისწინებული იყოს დასაქმების მოთხოვნებისთვის გამოცდილების, ხელმისაწვდომობისა და კლიენტის მოთხოვნების საფუძველზე.',
          },
          {
            q: 'მოქმედებთ როგორც საკრუინგო სააგენტო?',
            a: 'არა. Elvora გთავაზობთ დასაქმების მხარდაჭერასა და საკონსულტაციო მომსახურებას. დასაქმების გადაწყვეტილებები და კონტრაქტები ფორმდება უშუალოდ მეზღვაურებსა და დამსაქმებელ კომპანიებს შორის.',
          },
          {
            q: 'ახდევინებთ თუ არა საფასურს მეზღვაურებს?',
            a: 'ჩვენ არ ვახდევინებთ საფასურს მეზღვაურებს პროფილის გაგზავნისთვის. ზოგიერთი დამატებითი სერვისი, როგორიცაა კონსულტაცია დოკუმენტაციაზე ან კარიერული კონსალტინგი, შეიძლება შემოთავაზებული იყოს ცალკე მოთხოვნის შემთხვევაში.',
          },
          {
            q: 'რა ტიპის გემებსა და პროექტებზე მუშაობთ?',
            a: 'ჩვენ მხარს ვუჭერთ დასაქმების მოთხოვნებს საზღვაო და ოფშორულ სექტორებში, მათ შორის სავაჭრო გემებზე, ოფშორული ფლოტის გემებსა და საპროექტო ოპერაციებზე.',
          },
          {
            q: 'რამდენ ხანს გრძელდება განხილვის პროცესი?',
            a: 'პროფილის განხილვა დამოკიდებულია მიმდინარე დასაქმების აქტივობასა და კლიენტების მოთხოვნებზე. უკავშირდებიან მხოლოდ შერჩეულ კანდიდატებს.',
          },
          {
            q: 'როგორ დამიკავშირდებიან, თუ ჩემი პროფილი შესაფერისია?',
            a: 'შერჩეულ კანდიდატებს უკავშირდებიან პირდაპირ, პროფილის გაგზავნისას მითითებული საკონტაქტო მონაცემების გამოყენებით.',
          },
        ],
      },
      form: {
        pill: 'განაცხადი',
        title: 'მეზღვაურის პროფილის გაგზავნა',
        desc1:
          'გაგზავნეთ თქვენი პროფესიული პროფილი განსახილველად დასაქმების მხარდაჭერის პროცესის ფარგლებში.',
        desc2: 'გაგზავნა არ იძლევა დასაქმების გარანტიას.',
        labels: {
          firstName: 'სახელი',
          lastName: 'გვარი',
          rank: 'რანგი / პოზიცია',
          selectRank: 'აირჩიეთ რანგი/პოზიცია',
          specify: 'გთხოვთ დააზუსტოთ',
          enterPosition: 'შეიყვანეთ თქვენი პოზიცია',
          email: 'ელ. ფოსტა',
          phone: 'ტელეფონი / WhatsApp',
          phoneHint: 'მიუთითეთ ქვეყნის კოდი (მაგ., +1, +44, +995)',
          cv: 'CV-ს ატვირთვა (PDF, DOC ან სურათი)',
          dragDrop: 'გადმოიტანეთ თქვენი CV აქ ან დააჭირეთ ასარჩევად',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (მაქს. 48 MB)',
          vesselType: 'გამოცდილება გემის ტიპის მიხედვით',
          selectVessel: 'აირჩიეთ გემის ტიპი',
          yearsExp: 'გამოცდილების წლები',
          availability: 'მზადყოფნის თარიღი',
          availabilityHint: 'აირჩიეთ მზადყოფნის უადრესი თარიღი',
          consent:
            'ვადასტურებ, რომ მოწოდებული ინფორმაცია ზუსტია და ვეთანხმები ჩემი პერსონალური მონაცემების დამუშავებას დასაქმების მხარდაჭერის მიზნით.',
          submit: 'პროფილის გაგზავნა',
          disclaimer:
            'მთელი ინფორმაცია კონფიდენციალურია. უკავშირდებიან მხოლოდ შერჩეულ კანდიდატებს.',
        },
        ranks: {
          deck: 'გემბანის გუნდი',
          engine: 'სამანქანო გუნდი',
          other: 'სხვა',
          options: {
            master: 'კაპიტანი (Master)',
            chiefMate: 'უფროსი თანაშემწე (Chief Mate)',
            secondMate: 'მეორე თანაშემწე (Second Mate)',
            thirdMate: 'მესამე თანაშემწე (Third Mate)',
            bosun: 'ბოცმანი (Bosun)',
            ab: 'პირველი კლასის მატროსი (AB)',
            os: 'მეორე კლასის მატროსი (OS)',
            chiefEng: 'უფროსი მექანიკოსი (Chief Engineer)',
            secondEng: 'მეორე მექანიკოსი (Second Engineer)',
            thirdEng: 'მესამე მექანიკოსი (Third Engineer)',
            fourthEng: 'მეოთხე მექანიკოსი (Fourth Engineer)',
            eto: 'ელექტრომექანიკოსი (ETO)',
            asstEto: 'ელექტრომექანიკოსის ასისტენტი (Assistant ETO)',
            motorman: 'მოტორისტი (Motorman / Oiler)',
            wiper: 'ვაიპერი (Wiper)',
            dpo: 'DP ოპერატორი (DPO)',
            sdpo: 'უფროსი DP ოპერატორი (SDPO)',
            craneOp: 'ამწის ოპერატორი (Crane Operator)',
            rigger: 'სტროპალშიკი (Rigger)',
            electrician: 'ელექტრიკოსი (Electrician)',
            mechanic: 'მექანიკოსი (Mechanic)',
            other: 'სხვა (გთხოვთ დააზუსტოთ)',
          },
        },
        vessels: {
          tanker: 'ტანკერი',
          bulk: 'ბალკერი',
          container: 'კონტეინერმზიდი',
          offshore: 'ოფშორული ფლოტის გემი (OSV)',
          cruise: 'საკრუიზო გემი',
          other: 'სხვა',
        },
      },
      contact: {
        pill: 'კონტაქტი',
        title: 'გჭირდებათ დახმარება?',
        desc: 'დაგვიკავშირდით მესენჯერის ან ელ. ფოსტის საშუალებით სწრაფი დახმარებისთვის',
      },
    },
    companies: {
      hero: {
        title:
          'ეკიპაჟების საიმედო მიწოდება გემებისა და ოფშორული პროექტებისთვის',
        desc1:
          'პროფესიული რეკრუტინგი, სკრინინგი და ეკიპაჟის მართვა გემთმფლობელებისა და ოპერატორებისთვის.',
        desc2: 'ცალკეული პოზიციებიდან სრულ ეკიპაჟებამდე.',
        requestCrew: 'ეკიპაჟის მოთხოვნა',
        discussTask: 'დავალების განხილვა',
      },
      services: {
        pill: 'სერვისები',
        title: 'სერვისები გემთმფლობელებისა\nდა ოპერატორებისთვის',
        desc1:
          'ეკიპაჟის შერჩევისა და მართვის კომპლექსური გადაწყვეტილებები საზღვაო და ოფშორული ოპერაციებისთვის.',
        desc2:
          'კანდიდატების შერჩევიდან მოთხოვნებთან სრულ შესაბამისობამდე და ეკიპაჟის ლოგისტიკამდე.',
        items: {
          recruitment: {
            title: 'ეკიპაჟის შერჩევა',
            desc: 'რეკრუტინგის სრული ციკლი ყველა რანგისა და გემის ტიპისთვის — ოფშორული და სავაჭრო ფლოტი.',
          },
          screening: {
            title: 'სკრინინგი და შემოწმება',
            desc: 'დოკუმენტების შემოწმება, ბიოგრაფიის, გამოცდილების დადასტურება და მოთხოვნებთან შესაბამისობის სკრინინგი.',
          },
          compliance: {
            title: 'შესაბამისობის მართვა',
            desc: 'STCW, MLC, დროშის სახელმწიფოსა და კლიენტის სპეციფიკური მოთხოვნების სრული დაფარვა.',
          },
          coordination: {
            title: 'ეკიპაჟის კოორდინაცია',
            desc: 'ეკიპაჟის ცვლილებები, მგზავრობის მორგება, მობილიზაცია და ჩანაცვლების დაგეგმვა.',
          },
        },
      },
      steps: {
        pill: 'პროცესი',
        title: 'როგორ ვმუშაობთ',
        desc: 'ეკიპაჟის შერჩევის მკაფიო და გამჭვირვალე პროცესი — მოთხოვნიდან გემზე ასვლამდე',
        items: [
          {
            title: 'მოთხოვნა',
            desc: 'გამოგვიგზავნეთ თქვენი მოთხოვნები ეკიპაჟზე და გემის ან პროექტის დეტალები',
          },
          {
            title: 'სკრინინგი',
            desc: 'კანდიდატების შერჩევა, დოკუმენტების შემოწმება და მოთხოვნებთან შესაბამისობა',
          },
          {
            title: 'შერჩევა და ინტერვიუ',
            desc: 'კვალიფიციური კანდიდატების მოკლე სიის ფორმირება და ინტერვიუს ორგანიზება',
          },
          {
            title: 'მობილიზაცია',
            desc: 'კონტრაქტის ხელმოწერა, ეკიპაჟის მობილიზაცია და მიმდინარე კოორდინაცია',
          },
        ],
      },
      compliance: {
        pill: 'შესაბამისობა',
        title: 'შესაბამისობა და კონფიდენციალურობა',
        desc: 'ჩვენ ვმუშაობთ საერთაშორისო საზღვაო სტანდარტების სრული დაცვით და უზრუნველყოფთ მონაცემთა მკაცრ დაცვას',
        items: {
          nda: {
            title: 'NDA და მონაცემთა დაცვა',
            desc: 'კლიენტებისა და ეკიპაჟის მთელი ინფორმაცია მუშავდება მკაცრი კონფიდენციალურობისა და გაუმჟღავნებლობის პრინციპების დაცვით.',
          },
          verification: {
            title: 'დოკუმენტების შემოწმება',
            desc: 'სერტიფიკატების, ლიცენზიების, სამედიცინო ცნობებისა და გამოცდილების საფუძვლიანი შემოწმება წარდგენამდე.',
          },
          stcw: {
            title: 'STCW და MLC შესაბამისობა',
            desc: 'სრული შესაბამისობა STCW, MLC 2006, დროშის სახელმწიფოსა და კლიენტის სპეციფიკურ მოთხოვნებთან.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'ხშირი კითხვები',
        items: [
          {
            q: 'მოქმედებთ როგორც დამსაქმებელი ან საკრუინგო სააგენტო?',
            a: 'არა. Elvora გთავაზობთ რეკრუტინგისა და ოპერაციული მხარდაჭერის სერვისებს. შრომითი ხელშეკრულებები ფორმდება უშუალოდ ეკიპაჟსა და დამსაქმებელ კომპანიას შორის.',
          },
          {
            q: 'რა ტიპის გემებსა და პროექტებს უჭერთ მხარს?',
            a: 'ჩვენ მხარს ვუჭერთ სავაჭრო გემებსა და ოფშორულ პროექტებს, მათ შორის ტანკერებს, ოფშორული ფლოტის გემებსა და საპროექტო საზღვაო ოპერაციებს.',
          },
          {
            q: 'უზრუნველყოფთ ეკიპაჟის ცალკეულ წევრებს თუ სრულ ეკიპაჟებს?',
            a: 'ჩვენ მხარს ვუჭერთ როგორც მოთხოვნებს ცალკეულ პოზიციებზე, ისე გადაწყვეტილებებს სრული ან ნაწილობრივი ეკიპაჟებისთვის, პროექტის მოცულობისა და კლიენტის მოთხოვნების მიხედვით.',
          },
          {
            q: 'როგორ უზრუნველყოფთ კანდიდატების ხარისხსა და მოთხოვნებთან შესაბამისობას?',
            a: 'ყველა კანდიდატი გადის დოკუმენტების შემოწმებას, გამოცდილების განხილვას და შემოწმებას STCW, MLC, დროშის სახელმწიფოსა და კლიენტის სტანდარტებთან შესაბამისობაზე.',
          },
          {
            q: 'რამდენად სწრაფად შეიძლება ეკიპაჟის მობილიზება?',
            a: 'მობილიზაციის ვადები დამოკიდებულია გემის ტიპზე, პოზიციაზე, ხელმისაწვდომობასა და დოკუმენტების სტატუსზე. ვადები განიხილება ინდივიდუალურად თითოეული მოთხოვნისთვის.',
          },
          {
            q: 'მუშაობთ მთელ მსოფლიოში?',
            a: 'დიახ. ჩვენ მხარს ვუჭერთ საერთაშორისო პროექტებს და ვთანამშრომლობთ გემთმფლობელებთან და ოპერატორებთან სხვადასხვა რეგიონში.',
          },
        ],
      },
      form: {
        pill: 'მოთხოვნა',
        title: 'ეკიპაჟის მოთხოვნა ან პროექტის განხილვა',
        desc1:
          'გამოგვიგზავნეთ თქვენი მოთხოვნები ეკიპაჟზე ან პროექტის დეტალები.',
        desc2:
          'ჩვენ განვიხილავთ თითოეულ მოთხოვნას ინდივიდუალურად და გიპასუხებთ.',
        sections: {
          company: 'ინფორმაცია კომპანიის შესახებ',
          details: 'მოთხოვნის დეტალები',
          timeline: 'ვადები',
        },
        labels: {
          companyName: 'კომპანიის სახელი',
          contactPerson: 'საკონტაქტო პირი',
          email: 'ელ. ფოსტა',
          phone: 'ტელეფონი / WhatsApp',
          vesselType: 'გემის / პროექტის ტიპი',
          selectVessel: 'აირჩიეთ გემის/პროექტის ტიპი',
          positions: 'საჭირო პოზიციები',
          selectPositions: 'აირჩიეთ პოზიციები',
          crewNumber: 'ეკიპაჟის რაოდენობა',
          location: 'ლოკაცია / რეგიონი',
          selectLocation: 'აირჩიეთ რეგიონი (არასავალდებულო)',
          additional: 'დამატებითი მოთხოვნები',
          additionalPlaceholder:
            'სპეციფიკური სერტიფიკატები, მოთხოვნები გამოცდილებაზე, პროექტის დეტალები...',
          startDate: 'სასურველი დაწყების თარიღი',
          duration: 'კონტრაქტის ხანგრძლივობა',
          selectDuration: 'აირჩიეთ ხანგრძლივობა (არასავალდებულო)',
          consent:
            'ვადასტურებ, რომ მოწოდებული ინფორმაცია ზუსტია და ვეთანხმები მონაცემთა დამუშავებას რეკრუტინგისა და ოპერაციული მხარდაჭერის მიზნით.',
          submit: 'მოთხოვნის გაგზავნა',
          disclaimer:
            'მთელი ინფორმაცია მუშავდება კონფიდენციალურად NDA-ს შესაბამისად. ჩვენ გიპასუხებთ 24 საათის განმავლობაში.',
        },
        vesselTypes: [
          'სავაჭრო გემი',
          'ტანკერი',
          'ოფშორული ფლოტის გემი (OSV)',
          'Jack-Up / ოფშორული დანადგარი',
          'საპროექტო ოპერაცია',
          'სხვა',
        ],
        regions: [
          'მთელი მსოფლიო',
          'ევროპა',
          'ახლო აღმოსავლეთი',
          'აფრიკა',
          'აზია-წყნარი ოკეანის რეგიონი',
          'სხვა',
        ],
        durations: {
          m1: '1 თვე',
          m2: '2 თვე',
          m3: '3 თვე',
          m4: '4 თვე',
          m6: '6 თვე',
          m9: '9 თვე',
          m12: '12 თვე',
          ongoing: 'მუდმივი / უვადო',
          project: 'საპროექტო',
        },
      },
      contact: {
        pill: 'კონტაქტი',
        title: 'დაგვიკავშირდით',
        desc: 'დაგვიკავშირდით ეკიპაჟის მოთხოვნებისთვის, კონსულტაციებისთვის ან პარტნიორობის შესაძლებლობებისთვის',
      },
    },
  },
}
